/*
 * tc-gift-engine.js: the Traveler's Choice free gift engine, v2 (tiered gifts).
 *
 * Spec: live-theme/free-gift-workstream/2026-09-29-tier-gift-engine-design.md
 * (section 0 revision R1 overrides the rest; sections 3 and 5 are this file's
 * contract). Guardrail numbers (G1 to G14) refer to
 * live-theme/free-gift-workstream/2026-09-29-guardrail-map-code.md.
 * GitHub: TravelerChoiceT/shopify-theme issue #424 (epic #422).
 *
 * Loaded (deferred) by snippets/free-gift.liquid, which renders the config
 * JSON this file reads. Nothing here prices anything: pricing belongs to the
 * cubes BXGY (T0, authority "bxgy") and the tier-gift-discount Function
 * (authority "function"). This file only adds, swaps and removes gift lines,
 * and its price-only sweep guarantees nobody is ever charged for a gift line
 * the engine tagged, whoever was supposed to price it.
 *
 * Layout of this file:
 *   1. Decision core: a line-for-line port of the Function's
 *      extensions/tier-gift-discount/src/tier_gift_logic.js. Theme and
 *      Function MUST agree on every shared test vector
 *      (tests/tier-gift/tier-gift-vectors.json). Change both or neither.
 *   2. computeTargets(cart, cfg, today): the theme entry point to that core.
 *   3. Line planning: the price-only sweep, per-slot bookkeeping, stamps.
 *   4. The reconcile engine (busy flag plus one queued re-run).
 *   5. UI: tier cards on the cart page, the progress line and its ladder
 *      marks (cart page and drawer), and the PDP extras and gift ladder
 *      (issue #604).
 *   6. Boot, including the ?tc_gift_test= link.
 *
 * R1 (Kevin, 2026-09-29): for a cart that only earns T0, behaviour is
 * regression-identical to the single-gift engine this replaces. T0 slot 0 is
 * therefore driven by the legacy state machine (t0Branch) with its original
 * flags (_gift_auto_added, _gift_retry_used), its original stamp texts and its
 * original qualification (the server-rendered flags). The per-slot mechanics
 * (opt-out on removal, per-slot retry budget, upgrade swap, quantity clamp)
 * apply to every other slot.
 *
 * Node: `module.exports` exposes the same API so tests/tier-gift can run it.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.TCGiftEngine = api;
    api.autoBoot();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ======================================================================
   * 1. DECISION CORE: port of tier_gift_logic.js (keep in lockstep)
   * ====================================================================== */

  var LUGGAGE_COLLECTION_ID = 'gid://shopify/Collection/466650333409';
  // Qualifier collections compiled into the Function's input query. The theme
  // hard-codes the SAME map and applies the identical check, or the two would
  // disagree about which carts get tier gifts (design rule 10).
  var COMPILED_QUALIFIERS = Object.freeze({ luggage: LUGGAGE_COLLECTION_ID });

  var ACTIVE_VARIANT_TOKEN = '@active_variant';
  var T0_KEY = 't0';
  var AUTHORITY_FUNCTION = 'function';
  var AUTHORITY_BXGY = 'bxgy';
  var CODES_STACK = 'stack';
  var CODES_REJECT = 'reject';
  var MODE_TIERED = 'tiered';
  var MODE_LEGACY = 'legacy';

  var DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  var VARIANT_GID_RE = /^gid:\/\/shopify\/ProductVariant\/\d+$/;
  var TIMESTAMP_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:\d{2})$/;

  function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
  function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }
  function isDateString(value) {
    return typeof value === 'string' && DATE_RE.test(value);
  }
  function isVariantGid(value) {
    return typeof value === 'string' && VARIANT_GID_RE.test(value);
  }
  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim() !== '';
  }
  function isAbsent(value) {
    return value === undefined || value === null;
  }
  function parseJsonValue(value) {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return undefined;
    }
  }

  function parseTimestamp(value) {
    if (typeof value !== 'string') return null;
    var match = TIMESTAMP_RE.exec(value);
    if (!match) return null;
    var frac = match[7];
    var millis = frac ? Number((frac + '00').slice(0, 3)) : 0;
    var epoch = Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      match[6] ? Number(match[6]) : 0,
      millis
    );
    var zone = match[8];
    if (zone !== 'Z') {
      var sign = zone[0] === '-' ? -1 : 1;
      var offsetMinutes = Number(zone.slice(1, 3)) * 60 + Number(zone.slice(4, 6));
      epoch -= sign * offsetMinutes * 60 * 1000;
    }
    return isFinite(epoch) ? epoch : null;
  }

  // Design rule 6, neutral line shape: any non-empty _free_gift, or the
  // legacy Kite _free_product.
  function isGiftLine(line) {
    return isNonEmptyString(line && line.free_gift_attr) || isNonEmptyString(line && line.free_product_attr);
  }

  function isValidCandidateList(list) {
    return Array.isArray(list) && list.every(function (candidate) {
      return candidate === ACTIVE_VARIANT_TOKEN || isVariantGid(candidate);
    });
  }
  function isValidSlot(slot) {
    if (!isPlainObject(slot)) return false;
    if (!isAbsent(slot.phases)) {
      return Array.isArray(slot.phases) && slot.phases.every(function (phase) {
        return isPlainObject(phase) && isDateString(phase.starts) && isValidCandidateList(phase.candidates);
      });
    }
    return isValidCandidateList(slot.candidates);
  }
  function isValidTier(tier) {
    return isPlainObject(tier) &&
      isNonEmptyString(tier.key) &&
      Number.isInteger(tier.threshold_cents) &&
      tier.threshold_cents >= 0 &&
      (isAbsent(tier.authority) || tier.authority === AUTHORITY_FUNCTION || tier.authority === AUTHORITY_BXGY) &&
      Array.isArray(tier.slots) &&
      tier.slots.every(isValidSlot);
  }
  function isValidWindow(win) {
    return isPlainObject(win) &&
      isNonEmptyString(win.key) &&
      (isAbsent(win.starts) || isDateString(win.starts)) &&
      (isAbsent(win.ends) || isDateString(win.ends)) &&
      isNonEmptyString(win.qualifier) &&
      Array.isArray(win.tiers) &&
      win.tiers.every(isValidTier);
  }

  function validateConfig(config) {
    if (!isPlainObject(config) || config.schema_version !== 1) return null;
    if (!isAbsent(config.live_from) && !isDateString(config.live_from)) return null;
    if (!isAbsent(config.codes_policy) && config.codes_policy !== CODES_STACK && config.codes_policy !== CODES_REJECT) return null;
    if (!isPlainObject(config.qualifiers)) return null;
    if (!isAbsent(config.reinstated) && !isPlainObject(config.reinstated)) return null;
    if (!Array.isArray(config.windows) || !config.windows.every(isValidWindow)) return null;
    if (!isAbsent(config.force_window)) {
      if (typeof config.force_window !== 'string' || !config.windows.some(function (w) { return w.key === config.force_window; })) {
        return null;
      }
    }
    return config;
  }

  function selectWindow(config, today) {
    var windows = config.windows;
    if (!isAbsent(config.force_window)) {
      for (var f = 0; f < windows.length; f++) {
        if (windows[f].key === config.force_window) return windows[f];
      }
      return null;
    }
    var best = null;
    for (var i = 0; i < windows.length; i++) {
      var win = windows[i];
      var starts = isAbsent(win.starts) ? null : win.starts;
      var ends = isAbsent(win.ends) ? null : win.ends;
      var started = starts === null || starts <= today;
      var notEnded = ends === null || today < ends;
      if (!started || !notEnded) continue;
      if (best === null) { best = win; continue; }
      var bestStarts = isAbsent(best.starts) ? null : best.starts;
      // Strictly later wins; ties keep the earlier-listed window.
      if (starts !== null && (bestStarts === null || starts > bestStarts)) best = win;
    }
    return best;
  }

  function isRetired(gid, retired, reinstated) {
    if (!hasOwn(retired, gid)) return false;
    if (!hasOwn(reinstated, gid)) return true;
    var retiredAt = parseTimestamp(retired[gid]);
    var reinstatedAt = parseTimestamp(reinstated[gid]);
    if (retiredAt === null || reinstatedAt === null) return true;
    return !(reinstatedAt > retiredAt);
  }

  function resolveSlotGift(slot, today, ctx) {
    var candidates = [];
    if (!isAbsent(slot.phases)) {
      for (var p = 0; p < slot.phases.length; p++) {
        if (slot.phases[p].starts <= today) candidates = slot.phases[p].candidates;
      }
    } else {
      candidates = slot.candidates;
    }
    for (var c = 0; c < candidates.length; c++) {
      var gid = candidates[c] === ACTIVE_VARIANT_TOKEN ? ctx.activeVariant : candidates[c];
      if (!isVariantGid(gid)) continue;
      if (isRetired(gid, ctx.retired, ctx.reinstated)) continue;
      return gid;
    }
    return null;
  }

  // A tier has a gift when at least one of its slots resolves to a variant
  // (resolveSlotGift: @active_variant, dated phases, retirement and
  // reinstatement; never stock).
  function tierHasGift(tier, today, ctx) {
    for (var s = 0; s < tier.slots.length; s++) {
      if (resolveSlotGift(tier.slots[s], today, ctx) !== null) return true;
    }
    return false;
  }

  // T0 (unchanged), plus the highest non-t0 tier reached by the spend that
  // still HAS a gift (issue #609, step fallback): a step whose slots all
  // resolve to nothing (every candidate retired) is skipped as if it were not
  // configured, so the step below it wins. Only state.retired and
  // config.reinstated decide this, the same persisted data the Function
  // reads, so theme and Function always agree. Stock is never consulted here.
  function earnedTiers(tiers, spendCents, today, ctx) {
    var t0 = null;
    var spendTier = null;
    for (var i = 0; i < tiers.length; i++) {
      if (t0 === null && tiers[i].key === T0_KEY) t0 = tiers[i];
    }
    for (var j = 0; j < tiers.length; j++) {
      var tier = tiers[j];
      if (tier.key === T0_KEY || tier.threshold_cents > spendCents) continue;
      // Only a tier that would beat the current pick needs its gifts resolved
      // (same order as the Function's earnedTiers).
      if (spendTier !== null && tier.threshold_cents <= spendTier.threshold_cents) continue;
      if (tierHasGift(tier, today, ctx)) spendTier = tier;
    }
    return [t0, spendTier].filter(function (tier) { return tier !== null; });
  }

  function legacyResult(detail) {
    return { mode: MODE_LEGACY, window: null, earned: [], free_line_ids: [], codes_policy: CODES_STACK, detail: detail || {} };
  }
  function tieredResult(windowKey, codesPolicy, earned, freeLineIds, detail) {
    return { mode: MODE_TIERED, window: windowKey, earned: earned, free_line_ids: freeLineIds, codes_policy: codesPolicy, detail: detail };
  }

  /*
   * The whole decision (design sections 0 and 3), identical to the Function's
   * computeTierGifts except that the caller supplies tokenValid. The Function
   * compares the token itself; the theme never sees the token (Liquid compares
   * it at render time and prints only a boolean), so the browser passes that
   * boolean in. tests/tier-gift computes tokenValid exactly the way the
   * Function does, so the vectors exercise the same branch either way.
   *
   * `detail` is theme-only extra information (the active window object, the
   * effective date, spend, the claim map). It never changes the four fields
   * the vectors compare.
   */
  function decide(input, tokenValid, compiledQualifiers) {
    var compiled = compiledQualifiers || COMPILED_QUALIFIERS;
    var config = validateConfig(parseJsonValue(input && input.config));
    if (config === null) return legacyResult({ reason: 'config' });
    if (config.enabled !== true) return legacyResult({ reason: 'disabled' });

    var realToday = isDateString(input && input.today) ? input.today : null;
    if (realToday === null) return legacyResult({ reason: 'today' });
    var attributes = isPlainObject(input && input.cart_attributes) ? input.cart_attributes : {};
    if (!isAbsent(config.live_from) && realToday < config.live_from && !tokenValid) {
      return legacyResult({ reason: 'pre-live' });
    }
    var testDate = attributes._tc_gift_test_date;
    var today = tokenValid && isDateString(testDate) ? testDate : realToday;
    var codesPolicy = isAbsent(config.codes_policy) ? CODES_STACK : config.codes_policy;

    var win = selectWindow(config, today);
    var detail = { reason: null, config: config, today: today, window: win, qualifierKey: null, spendCents: 0, qualifies: false, ctx: null, claims: {}, claimedBy: [] };
    if (win === null) return tieredResult(null, codesPolicy, [], [], detail);

    var qualifierKey = win.qualifier;
    var qualifierEntry = config.qualifiers[qualifierKey];
    var compiledId = hasOwn(compiled, qualifierKey) ? compiled[qualifierKey] : null;
    if (!isPlainObject(qualifierEntry) || typeof compiledId !== 'string' || qualifierEntry.collection_id !== compiledId) {
      return legacyResult({ reason: 'qualifier' });
    }
    detail.qualifierKey = qualifierKey;

    var lines = Array.isArray(input && input.lines) ? input.lines : [];
    var qualifies = false;
    var spendCents = 0;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (isGiftLine(line)) continue;
      if (line && line.product_in && line.product_in[qualifierKey] === true) qualifies = true;
      var cents = line ? line.subtotal_cents : null;
      if (Number.isInteger(cents) && cents > 0) spendCents += cents;
    }
    detail.spendCents = spendCents;
    detail.qualifies = qualifies;

    var stateValue = parseJsonValue(input && input.state);
    var retired = isPlainObject(stateValue) && isPlainObject(stateValue.retired) ? stateValue.retired : {};
    var reinstated = isPlainObject(config.reinstated) ? config.reinstated : {};
    var activeVariant = isVariantGid(input && input.active_variant) ? input.active_variant : null;
    var ctx = { activeVariant: activeVariant, retired: retired, reinstated: reinstated };
    detail.ctx = ctx;
    if (!qualifies) return tieredResult(win.key, codesPolicy, [], [], detail);

    var earned = [];
    var freeLineIds = [];
    var claimed = {};
    earnedTiers(win.tiers, spendCents, today, ctx).forEach(function (tier) {
      var authority = isAbsent(tier.authority) ? AUTHORITY_FUNCTION : tier.authority;
      tier.slots.forEach(function (slot, index) {
        var variantId = resolveSlotGift(slot, today, ctx);
        if (variantId === null) return;
        var entry = { tier: tier.key, slot: index, variant_id: variantId, authority: authority };
        earned.push(entry);
        var found = null;
        for (var k = 0; k < lines.length; k++) {
          var candidate = lines[k];
          if (isGiftLine(candidate) && candidate.variant_id === variantId && typeof candidate.id === 'string' && !hasOwn(claimed, candidate.id)) {
            found = candidate;
            break;
          }
        }
        detail.claimedBy.push(found ? found.id : null);
        if (!found) return;
        claimed[found.id] = earned.length - 1;
        if (authority === AUTHORITY_FUNCTION) freeLineIds.push(found.id);
      });
    });
    detail.claims = claimed;
    return tieredResult(win.key, codesPolicy, earned, freeLineIds, detail);
  }

  // The Function's own entry point, token included. Used by tests only.
  function computeTierGifts(input, compiledQualifiers) {
    var attributes = isPlainObject(input && input.cart_attributes) ? input.cart_attributes : {};
    var token = input && input.test_token;
    var tokenValid = isNonEmptyString(token) && attributes._tc_gift_test === token;
    return decide(input, tokenValid, compiledQualifiers);
  }

  /* ======================================================================
   * 2. computeTargets: theme entry point
   * ====================================================================== */

  function variantGid(id) {
    return 'gid://shopify/ProductVariant/' + id;
  }
  function numericId(gid) {
    var match = /(\d+)$/.exec(String(gid == null ? '' : gid));
    return match ? Number(match[1]) : null;
  }
  function propsOf(item) {
    return (item && item.properties) || {};
  }
  function propString(value) {
    return value === undefined || value === null ? '' : String(value);
  }

  // /cart.js items to the neutral line shape. `qualifiers` is the published
  // frozen set per qualifier key ({luggage: {products: [ids]}}); /cart.js
  // cannot carry collection membership, so the server publishes it (G2, #348).
  function neutralLines(items, qualifiers) {
    var sets = {};
    Object.keys(qualifiers || {}).forEach(function (key) {
      var set = {};
      ((qualifiers[key] && qualifiers[key].products) || []).forEach(function (id) { set[String(id)] = true; });
      sets[key] = set;
    });
    return (items || []).map(function (item) {
      var productIn = {};
      Object.keys(sets).forEach(function (key) {
        productIn[key] = hasOwn(sets[key], String(item.product_id));
      });
      var props = propsOf(item);
      return {
        id: item.key,
        variant_id: variantGid(item.variant_id != null ? item.variant_id : item.id),
        product_in: productIn,
        subtotal_cents: item.original_line_price,
        quantity: item.quantity,
        free_gift_attr: props._free_gift === undefined ? null : props._free_gift,
        free_product_attr: props._free_product === undefined ? null : props._free_product
      };
    });
  }

  /*
   * computeTargets(cart, cfg, today)
   *   cart   {lines: [neutral lines], attributes: {_tc_gift_test, _tc_gift_test_date}}
   *   cfg    {config, state, activeVariant, testMode}
   *          testMode is the Liquid-computed token check (never the token).
   *   today  the real store-local date, YYYY-MM-DD
   * Returns the Function-shaped decision: {mode, window, earned,
   * free_line_ids, codes_policy} plus a theme-only `detail`.
   */
  function computeTargets(cart, cfg, today) {
    var c = cfg || {};
    return decide({
      today: today,
      config: c.config,
      state: c.state,
      active_variant: c.activeVariant,
      cart_attributes: (cart && cart.attributes) || {},
      lines: (cart && cart.lines) || []
    }, !!c.testMode, c.compiledQualifiers);
  }

  // Store-local date (America/Los_Angeles), never Liquid `now`: page cache
  // can be hours stale (design rule 1).
  function storeToday(date) {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(date || new Date());
      var map = {};
      parts.forEach(function (part) { map[part.type] = part.value; });
      var value = map.year + '-' + map.month + '-' + map.day;
      return isDateString(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  /* ======================================================================
   * 3. LINE PLANNING
   * ====================================================================== */

  // Stamp texts. The first four are the legacy engine's, byte for byte (R1):
  // t0Branch READS them back as state, so they must never change.
  var DECISION = 'Free gift decision';
  var STAMP_ADDED = 'Gift added automatically at $0';
  var STAMP_READDED = 'Re-added the gift from the cart';
  var STAMP_REMOVED = 'Removed the gift in cart';
  var STAMP_SWEPT = 'Gift removed automatically: it was no longer free';
  // New T0 text, written only where the legacy engine wrote a FALSE "added"
  // stamp after a rejected /cart/add.js (G6 fix). t0Branch does not read it,
  // so it can never trigger a branch.
  var STAMP_NOT_ADDED = 'Gift not added automatically: it was out of stock or unavailable';
  // Tier slots stamp a separate human-only attribute. Writing them into
  // "Free gift decision" would overwrite the text t0Branch reads as state and
  // break T0's opt-out and one-shot restore (R1).
  var TIER_DECISION = 'Tier gift decision';
  var RETRY_LIMIT = 2; // same as the legacy engine: the first add plus one restore

  function slotKey(tier, slot) {
    return (String(tier) + '_' + String(slot)).toLowerCase().replace(/[^a-z0-9_-]/g, '-');
  }
  function tierOf(item) {
    return propString(propsOf(item)._free_gift_tier) || T0_KEY;
  }
  function slotOf(item) {
    return propString(propsOf(item)._free_gift_slot) || '0';
  }
  function isLegacySlot(entry) {
    return entry.tier === T0_KEY && Number(entry.slot) === 0;
  }
  // T0 slot-0 lines (no tier tag, which is every line added before v2, or
  // tier t0 slot 0) keep the legacy rule: never removed while free, swept when
  // charged. Every other line, a T0 slot-1 line included, is per-slot managed.
  function isLegacyLine(item) {
    return tierOf(item) === T0_KEY && slotOf(item) === '0';
  }
  function t0Props() {
    return { _free_gift: 'true', _free_gift_tier: T0_KEY, _free_gift_slot: '0' };
  }
  // A line the engine (or the cart card's claim) added for T0 slot 0: it
  // carries the `_free_gift` tag the sweep keys on, and is a legacy line
  // (t0Props above, or a pre-v2 line with no tier or slot tag). An untagged
  // line of the gift product is a shopper's own purchase, never ours (#680).
  function isEngineT0Line(item) {
    return !!propsOf(item)._free_gift && isLegacyLine(item);
  }
  function tierProps(entry) {
    return { _free_gift: 'true', _free_gift_tier: String(entry.tier), _free_gift_slot: String(entry.slot) };
  }
  function isFreeLine(item) {
    return !!item && item.final_line_price === 0;
  }

  function formatMoney(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    var whole = Math.floor(amount) === amount;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return '$' + (whole ? String(amount) : amount.toFixed(2));
    }
  }
  // $0.00 written out in full, as the cart line and the terms show it
  // ("added to your cart at $0.00"); formatMoney would print "$0".
  function formatZero(currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(0);
    } catch (error) {
      return '$0.00';
    }
  }

  function poolEntry(cfg, variantId) {
    var pool = (cfg && cfg.tier && cfg.tier.pool) || {};
    return pool[String(numericId(variantId))] || null;
  }
  /*
   * A gift's name as it reads in copy (live QA on PR #426: "a free The Travel
   * Pillow"). Everywhere, a " · Default Title" variant suffix is dropped. In
   * running copy (inSentence), a leading "The " is dropped too, because the
   * sentence brings its own article ("a free", "the", "your free"). Real
   * variant names (" · Gray") are kept. For the T0 cubes title
   * ("Froster 3-Piece Packing Cubes · Charcoal Black") this is a no-op, and
   * the T0 copy is Liquid-rendered and never passes through here anyway (R1).
   */
  function displayName(title, inSentence) {
    var name = String(title == null ? '' : title).replace(/\s*·\s*Default Title\s*$/i, '');
    if (inSentence) name = name.replace(/^the\s+/i, '');
    return name;
  }
  // Staff stamps name gifts in running text too ("Removed the X gift in cart").
  function giftTitle(cfg, variantId) {
    var entry = poolEntry(cfg, variantId);
    return entry && entry.title ? displayName(entry.title, true) : 'variant ' + numericId(variantId);
  }
  function findTier(win, key) {
    if (!win || !Array.isArray(win.tiers)) return null;
    for (var i = 0; i < win.tiers.length; i++) {
      if (win.tiers[i].key === key) return win.tiers[i];
    }
    return null;
  }

  function stampTierAdded(cfg, entry, tier) {
    return 'Gift added automatically at $0: ' + giftTitle(cfg, entry.variant_id) + ' (' + entry.tier + ', ' + formatMoney(tier ? tier.threshold_cents : 0) + ' and up)';
  }
  function stampTierSwap(cfg, entry, tier, oldThreshold) {
    var verb = tier && tier.threshold_cents < oldThreshold ? 'changed' : 'upgraded';
    return 'Gift ' + verb + ' to ' + giftTitle(cfg, entry.variant_id) + ' at ' + formatMoney(tier ? tier.threshold_cents : 0);
  }
  function stampTierNotEarned(cfg, item) {
    return 'Gift removed automatically: ' + giftTitle(cfg, variantGid(item.variant_id)) + ' is no longer earned';
  }
  function stampTierCharged(cfg, item) {
    return 'Gift removed automatically: ' + giftTitle(cfg, variantGid(item.variant_id)) + ' was no longer free';
  }
  function stampTierDuplicate(cfg, item) {
    return 'Gift removed automatically: a second ' + giftTitle(cfg, variantGid(item.variant_id)) + ' gift line';
  }
  function stampTierRemoved(cfg, entry) {
    return 'Removed the ' + giftTitle(cfg, entry.variant_id) + ' gift in cart';
  }
  function stampTierReadded(cfg, entry) {
    return 'Re-added the ' + giftTitle(cfg, entry.variant_id) + ' gift from the cart';
  }
  function stampTierNotAdded(cfg, entry) {
    return 'Gift not added automatically: ' + giftTitle(cfg, entry.variant_id) + ' was out of stock or unavailable';
  }

  /*
   * snapshot(cart, cfg, today): the decision for this cart plus the T0 slot-0
   * description the legacy state machine needs.
   *   legacy mode: T0 = the Liquid-resolved gift (active_variant, or the
   *                free-gift-item fallback), exactly as before.
   *   tiered mode: T0 = the active window's t0 slot 0 gift, resolved with the
   *                vector rules (the same variant as active_variant for the
   *                evergreen and PBDD windows), qualified by the SERVER flags
   *                for the window's qualifier key, as the legacy engine was.
   */
  function snapshot(cart, cfg, today) {
    var tier = cfg.tier || {};
    var qualifiers = tier.qualifiers || {};
    var attrs = (cart && cart.attributes) || {};
    var testMode = !!cfg.testMode && isNonEmptyString(attrs._tc_gift_test);
    var decision = computeTargets(
      { lines: neutralLines(cart && cart.items, qualifiers), attributes: attrs },
      { config: tier.config, state: tier.state, activeVariant: tier.activeVariant, testMode: testMode },
      today
    );
    var t0 = null;
    if (decision.mode !== MODE_TIERED) {
      if (cfg.legacyReady) {
        t0 = {
          variantId: cfg.giftVariantId,
          productId: cfg.giftProductId,
          cartQualifies: !!cfg.cartQualifies,
          pageQualifies: !!cfg.pageQualifies
        };
      }
    } else if (decision.detail.window) {
      var win = decision.detail.window;
      var t0tier = findTier(win, T0_KEY);
      var slot0 = t0tier && t0tier.slots[0];
      // detail.ctx is set once the window's qualifier check passes, which is
      // always true here (a failed check returns legacy).
      var gid = slot0 && decision.detail.ctx ? resolveSlotGift(slot0, decision.detail.today, decision.detail.ctx) : null;
      if (gid) {
        var vid = numericId(gid);
        var entry = poolEntry(cfg, gid);
        var flags = qualifiers[win.qualifier] || {};
        t0 = {
          variantId: vid,
          productId: vid === cfg.giftVariantId ? cfg.giftProductId : (entry ? entry.productId : null),
          cartQualifies: !!flags.cartQualifies,
          pageQualifies: !!flags.pageQualifies
        };
      }
    }
    return { decision: decision, mode: decision.mode, t0: t0 };
  }

  /*
   * May the legacy T0 claim card (snippets/free-gift-cart-cards.liquid) offer
   * its gift? (#479 item 2.) That card can only add one variant, the
   * Liquid-resolved T0 gift. In legacy mode it is the legacy card and nothing
   * changes. In tiered mode it may offer the claim only when the active
   * window's T0 slot 0 resolves to that same variant: a window with no t0
   * tier, no active window, an unresolved @active_variant (the metafield is
   * blank), or a different T0 gift (a BFCM kit with function authority) all
   * mean the card would add a gift this cart has not earned. Availability is
   * not judged here: T0 keeps the legacy behaviour for that (R1).
   */
  function legacyT0Offered(snap, variantId) {
    if (!snap || snap.mode !== MODE_TIERED) return true;
    var wanted = numericId(variantId);
    return !!(snap.t0 && wanted !== null && Number(snap.t0.variantId) === wanted);
  }

  /*
   * The EVENT T0 claim target (#765): what the cart card's "Add free gift" may
   * offer when the active window's T0 slot 0 is another gift than the one the
   * card is rendered for (BFCM 2026: the Amenities Kit, while
   * tc_free_gift.active_variant stays on the cubes). Without it a shopper who
   * removed the kit could not add it back: legacyT0Offered rightly withdrew the
   * cubes claim, and nothing offered the kit.
   *
   * Non-null only when ALL of these hold, so the card can fail closed on null:
   *   - tiered mode, with a T0 slot 0 that resolves to a variant;
   *   - that variant is not cfg.giftVariantId (the card's own gift: its claim is
   *     the legacy one, untouched, R1);
   *   - this cart has EARNED it (decision.earned has the T0 slot 0 entry: the
   *     cart qualifies for the window) and its authority is "function". A
   *     "bxgy" T0 other than active_variant would be priced by nothing, so it is
   *     never offered;
   *   - the gift pool has an entry with a title for it.
   * `available` is the pool's own flag (render-time, never stock): the card
   * retargets only on true. Display fields come from the same pool the engine
   * already prints for the PDP and the tier cards; nothing here prices anything.
   */
  function eventT0Target(snap, cfg) {
    if (!snap || snap.mode !== MODE_TIERED || !snap.t0) return null;
    var vid = Number(snap.t0.variantId);
    if (!isFinite(vid) || vid === Number(cfg && cfg.giftVariantId)) return null;
    var earned = (snap.decision && snap.decision.earned) || [];
    var slot0 = null;
    for (var i = 0; i < earned.length; i++) {
      if (earned[i].tier === T0_KEY && Number(earned[i].slot) === 0) { slot0 = earned[i]; break; }
    }
    if (!slot0 || slot0.authority !== AUTHORITY_FUNCTION || numericId(slot0.variant_id) !== vid) return null;
    var entry = poolEntry(cfg, variantGid(vid));
    if (!entry || !isNonEmptyString(entry.title)) return null;
    return {
      variantId: vid,
      productId: entry.productId === undefined ? null : entry.productId,
      title: entry.title,
      image: entry.image || '',
      price: entry.price,
      available: entry.available === true
    };
  }

  /*
   * Is the engine's own T0 add still DUE for this cart (#765)? True exactly
   * when t0Branch would post /cart/add.js on its next pass: no line of the T0
   * gift product, the cart qualifies, and either no opt-out flag yet (a cart
   * that has just qualified) or the one guard retry after a sweep is unspent.
   * The cart card holds its event-gift claim back while this is true AND a run
   * is in flight or scheduled (the instance's t0AddPending), so a shopper who
   * has just added luggage never sees "Add free gift" for the second or two
   * before the engine adds the gift itself. "Due" alone is not enough: after a
   * sweep of a charged T0 line the cart is due (guard retry unspent) but no run
   * is coming (Codex P1 on 86239e22), and then the claim must show. A removal
   * leaves the flag set, so after one this is false and the claim shows. It
   * mirrors t0Branch's two add branches; a test pins the two together.
   */
  function t0AddDue(cart, snap) {
    var t0 = snap && snap.t0;
    if (!t0 || !t0.variantId || !cart) return false;
    var items = cart.items || [];
    var attrs = cart.attributes || {};
    var isGiftProduct = function (i) {
      return t0.productId != null ? i.product_id === t0.productId : i.variant_id === t0.variantId;
    };
    if (items.some(isGiftProduct)) return false;
    var qualifies = items.some(function (i) { return !isGiftProduct(i); }) && (t0.cartQualifies || t0.pageQualifies);
    if (!qualifies) return false;
    if (!attrs._gift_auto_added) return true;
    return (attrs[DECISION] || '') === STAMP_SWEPT && !attrs._gift_retry_used;
  }

  // Lines the price-only sweep may touch: exactly the legacy isTaggedGift
  // (FG:163-174): ours (_free_gift, on ANY product), or a legacy Kite
  // _free_product line on the current T0 gift product.
  function isSweepTagged(item, cfg) {
    var props = propsOf(item);
    if (props._free_gift) return true;
    return item.product_id === cfg.giftProductId && !!props._free_product;
  }

  /*
   * The price-only sweep (G4), planned as ONE batched /cart/update.js.
   *
   * The engine NEVER removes a FREE tagged line because of its own
   * qualification or target computation. The theme's view of qualification is
   * a frozen, per-render set (a bag added where no page rendered it is not in
   * it), while the pricing authorities (BXGY, Function) see the live cart, so a
   * line they are still freeing is, by definition, still earned. Removal is
   * only ever for:
   *   (a) a tagged line that is not free (the price sweep, every mode);
   *   (b) a tier line with quantity above 1: set to 1, and sweepPass re-checks
   *       its price in the same reconcile pass;
   *   (c) a second tagged line for the same tier slot (by its own
   *       _free_gift_tier / _free_gift_slot) after the first free one.
   * A superseded or unearned tier line needs no rule of its own: the Function
   * frees only the earned tier's active gift, so such a line turns charged and
   * (a) removes it. Target computation decides ADDS only (tierBranch), and an
   * unknown product blocks adds but never removes anything.
   *
   * T0 slot-0 lines keep the legacy rule exactly (R1): swept only when charged,
   * never clamped, never de-duplicated (the BXGY frees one unit, so a second
   * copy is charged and (a) takes it, as before).
   *
   * Bookkeeping for a charged tier line: if an earned slot claims it (the
   * vectors' claim rule), the discount failed to free an earned gift, which
   * spends one of that slot's retries. If no earned slot claims it, it was
   * released (superseded or unearned): no retry is spent, and it is recorded
   * so an add in the same pass is stamped as an upgrade swap.
   */
  function planSweep(cart, cfg, snap) {
    var updates = {};
    var attrs = {};
    var any = false;
    var t0Swept = false;
    var clamped = false;
    var events = [];
    var keptBySlot = {};
    var current = (cart && cart.attributes) || {};
    var tiered = snap.mode === MODE_TIERED;
    var claims = tiered ? snap.decision.detail.claims : {};
    var earned = snap.decision.earned;
    (cart.items || []).forEach(function (item) {
      if (!isSweepTagged(item, cfg)) return;
      var charged = item.final_line_price !== 0;
      if (isLegacyLine(item)) {
        if (!charged) return;
        updates[item.key] = 0;
        any = true;
        t0Swept = true;
        return;
      }
      var ownKey = slotKey(tierOf(item), slotOf(item));
      var claimIndex = hasOwn(claims, item.key) ? claims[item.key] : -1;
      if (item.quantity > 1) {
        // (b) Its price is checked again right after the clamp (sweepPass).
        updates[item.key] = 1;
        any = true;
        clamped = true;
        keptBySlot[ownKey] = true;
        return;
      }
      if (charged) {
        // (a)
        updates[item.key] = 0;
        any = true;
        events.push({ item: item, reason: claimIndex >= 0 ? 'charged' : 'released', entry: claimIndex >= 0 ? earned[claimIndex] : null });
        return;
      }
      if (keptBySlot[ownKey]) {
        // (c)
        updates[item.key] = 0;
        any = true;
        events.push({ item: item, reason: 'duplicate', entry: null });
        return;
      }
      keptBySlot[ownKey] = true;
    });
    if (t0Swept) attrs[DECISION] = STAMP_SWEPT;
    var superseded = [];
    events.forEach(function (ev) {
      if (ev.reason === 'duplicate') {
        attrs[TIER_DECISION] = stampTierDuplicate(cfg, ev.item);
        return;
      }
      var ownKey = slotKey(tierOf(ev.item), slotOf(ev.item));
      attrs['_gift_added_' + ownKey] = '';
      if (ev.reason === 'charged') {
        var retryKey = slotKey(ev.entry.tier, ev.entry.slot);
        var tries = parseInt(current['_gift_retry_' + retryKey], 10) || 0;
        attrs['_gift_retry_' + retryKey] = String(tries + 1);
        attrs[TIER_DECISION] = stampTierCharged(cfg, ev.item);
        return;
      }
      // released: charged, and no earned slot claims it
      attrs[TIER_DECISION] = tiered ? stampTierNotEarned(cfg, ev.item) : stampTierCharged(cfg, ev.item);
      if (tiered && tierOf(ev.item) !== T0_KEY) {
        // Only a spend tier can be the old side of an upgrade swap.
        var win = snap.decision.detail && snap.decision.detail.window;
        var oldTier = findTier(win, tierOf(ev.item));
        superseded.push({ tier: tierOf(ev.item), threshold: oldTier ? oldTier.threshold_cents : null });
      }
    });
    return { updates: updates, attrs: attrs, any: any, t0Swept: t0Swept, clamped: clamped, superseded: superseded };
  }

  // Cart attributes the empty-cart re-offer clears (G8): every `_gift_`
  // key plus both human stamps. For a T0-only cart that is exactly the legacy
  // list (_gift_auto_added, _gift_retry_used, "Free gift decision"). The test
  // attributes start `_tc_gift`, so a test session survives an empty cart.
  function reofferKeys(attributes) {
    var keys = [];
    Object.keys(attributes || {}).forEach(function (key) {
      var value = attributes[key];
      if (!value) return;
      if (key.indexOf('_gift_') === 0 || key === DECISION || key === TIER_DECISION) keys.push(key);
    });
    return keys;
  }

  /* ======================================================================
   * 4. THE RECONCILE ENGINE
   * ====================================================================== */

  /*
   * ONE RULE FOR EVERY CART REQUEST (Codex re-review of PR #426): a response
   * that resolves but is not 2xx is a failure, never a success.
   *   /cart.js          rejects. A server error must never be read as an
   *                     empty cart (that would run the re-offer and clear the
   *                     shopper's opt-outs).
   *   /cart/update.js   rejects (sweeps and bookkeeping alike). The pass is
   *                     marked failed, its finally-sweep runs, and run()
   *                     schedules one bounded follow-up.
   *   /cart/add.js      a 4xx is Shopify REFUSING the add (sold out, variant
   *                     gone): nothing was added, and the caller records the
   *                     refusal (stamp, flag, retry). A 5xx is AMBIGUOUS (the
   *                     add may have landed), so it also fails the pass:
   *                     finally-sweep plus follow-up settle it.
   * The engine sends no /cart/change.js (sweeps are batched update.js).
   */
  function httpError(url, r) {
    var error = new Error('tc-gift-engine: ' + url + ' answered HTTP ' + (r && r.status));
    error.status = r && r.status;
    return error;
  }
  function requireOk(url) {
    return function (r) {
      if (!r || !r.ok) throw httpError(url, r);
      return r;
    };
  }
  function isRefusal(r) {
    return !!r && !r.ok && r.status >= 400 && r.status < 500;
  }
  function requireCart(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('tc-gift-engine: /cart.js returned no cart');
    return cart;
  }
  // The cart API over any fetch-compatible function (the browser's, or the
  // test harness's fake). readCart applies the rule above; post resolves with
  // the raw response so each caller applies it for its endpoint. postQuiet is
  // post through `quietFetchFn()` when that returns a function (a fetch no
  // third-party script has wrapped, see applySweep), else plain post.
  function makeApi(fetchFn, quietFetchFn) {
    function send(f, url, body) {
      return f(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    }
    return {
      readCart: function () {
        return fetchFn('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(requireOk('/cart.js'))
          .then(function (r) { return r.json(); })
          .then(requireCart);
      },
      post: function (url, body) {
        return send(fetchFn, url, body);
      },
      postQuiet: function (url, body) {
        var quiet = quietFetchFn ? quietFetchFn() : null;
        return send(quiet || fetchFn, url, body);
      }
    };
  }

  /*
   * env: {
   *   cfg,                         the parsed config JSON
   *   api: {readCart(), post(url, body)}  see makeApi; post resolves the raw
   *                                response {ok, status, json()}
   *   dispatch(name, detail),      document CustomEvent
   *   setTimeout(fn, ms),
   *   today(),                     store-local date
   *   log(error)                   optional
   * }
   */
  function createEngine(env) {
    var cfg = env.cfg || {};
    var api = env.api;
    var busy = false;
    var rerun = false;
    var subscribers = [];
    var runs = 0;

    function today() {
      return env.today();
    }
    function notify(cart) {
      if (!cart || !cart.items) return;
      subscribers.forEach(function (fn) {
        try { fn(cart); } catch (error) { if (env.log) env.log(error); }
      });
    }
    function readCart() {
      return api.readCart().then(requireCart);
    }
    function setAttrs(attributes) {
      return api.post('/cart/update.js', { attributes: attributes }).then(requireOk('/cart/update.js'));
    }
    function obj(key, value) {
      var o = {};
      o[key] = value;
      return o;
    }

    // Busy flag kept (concurrency notes), but an overlapping trigger now
    // queues ONE re-run instead of being dropped. The engine never
    // re-triggers itself: its own theme:cart:change carries tcGiftEngine and
    // the listener in boot() ignores it; theme:cart:reload is handled by
    // theme.js CartItems (loadHTML only) and by the cart cards.
    //
    // A run that hit an error (a failed read or write anywhere in the chain)
    // schedules ONE follow-up run, so a dropped write never waits for the
    // shopper's next cart event. Bounded: at most MAX_ERROR_FOLLOW_UPS in a
    // row, reset by the next clean run, so a cart that cannot be read at all
    // (offline) does not poll forever.
    var errorFollowUps = 0;
    var MAX_ERROR_FOLLOW_UPS = 2;
    var ERROR_FOLLOW_UP_MS = 1500;
    // Every deferred run goes through schedule(), so isSettled() can tell the
    // UI that no run is in flight or on its way (the progress line waits for
    // that, so it never describes a tier swap before the swapped card shows).
    var pending = 0;
    // A sweep failed and may have landed: the next publish must reload the
    // cart rows even if its own pass changed nothing (applyPlan, #479).
    var linesNotice = false;
    function schedule(ms) {
      pending += 1;
      env.setTimeout(function () {
        pending -= 1;
        run();
      }, ms);
    }
    function isSettled() {
      return !busy && !rerun && pending === 0;
    }
    function run() {
      if (busy) {
        rerun = true;
        return Promise.resolve();
      }
      busy = true;
      runs += 1;
      var chain;
      try {
        chain = reconcile();
      } catch (error) {
        chain = Promise.reject(error);
      }
      return Promise.resolve(chain).then(function (outcome) {
        return outcome && outcome.error ? outcome.error : null;
      }, function (error) {
        return error || new Error('tc-gift-engine: reconcile failed');
      }).then(function (error) {
        busy = false;
        if (error) {
          if (env.log) env.log(error);
          if (errorFollowUps < MAX_ERROR_FOLLOW_UPS) {
            errorFollowUps += 1;
            rerun = false; // the follow-up also covers any queued trigger
            schedule(ERROR_FOLLOW_UP_MS);
            return;
          }
        } else {
          errorFollowUps = 0;
        }
        if (rerun) {
          rerun = false;
          schedule(300);
        }
        // The only place the engine becomes settled: busy just went false and
        // nothing was queued or scheduled above.
        if (isSettled()) fireSettled();
      });
    }

    // Callers waiting for the engine to finish its work (#765: the cart card's
    // held claim, Codex P2 on e79fd322). Called once, on the next settle, with
    // no timer and no cap: a request that never answers keeps the engine busy,
    // and the caller waits exactly as long. Already settled: called on the
    // next tick.
    var settleWaiters = [];
    function whenSettled(fn) {
      if (typeof fn !== 'function') return;
      if (isSettled()) { env.setTimeout(fn, 0); return; }
      settleWaiters.push(fn);
    }
    function fireSettled() {
      if (!settleWaiters.length) return;
      var waiting = settleWaiters;
      settleWaiters = [];
      waiting.forEach(function (fn) {
        try { fn(); } catch (error) { if (env.log) env.log(error); }
      });
    }

    // A sweep that answers non-2xx rejects (the rule above): nothing in its
    // body is assumed to have landed, its stamps included, and the pass's
    // finally-sweep and run()'s follow-up take over.
    //
    // It is sent with api.postQuiet when the page has one: a request that
    // third-party cart hooks do not see. Swym wraps window.fetch and XHR and
    // opens "Do you want to save this product for later?" (taking focus)
    // whenever a /cart/change or /cart/update response shows exactly one
    // product gone. A gift line the engine removes is not a shopper removal,
    // so Swym must not ask (NVDA capture on PR #426). The very next read goes
    // through window.fetch, which keeps Swym's own cart snapshot in step, so
    // a real shopper removal afterwards still gets Swym's question.
    function applySweep(plan) {
      var body = { updates: plan.updates };
      if (Object.keys(plan.attrs).length) body.attributes = plan.attrs;
      var send = api.postQuiet || api.post;
      return send.call(api, '/cart/update.js', body).then(requireOk('/cart/update.js'));
    }

    // Apply one sweep plan and hand back the cart as it now is.
    //
    // A sweep that FAILS may still have landed (#479 item 1): Shopify can
    // apply a removal or a clamp and answer 5xx, or the connection can drop
    // after the write. Nothing in the pass then marks the lines as changed,
    // and the finally-sweep and the follow-up both read a cart that is
    // already clean, so no theme:cart:reload would fire and the cart rows and
    // subtotal would stay stale until the shopper's next action. A failed
    // sweep therefore leaves a line-change notice on the ENGINE, not on the
    // pass: the publish step of this pass (or, when this pass cannot read the
    // cart, of a later one) sends the fresh cart and the reload, and only
    // then clears it. A notice for a sweep that did nothing costs one extra
    // re-render of the cart rows, never a wrong cart.
    function applyPlan(plan, st) {
      st.superseded = st.superseded.concat(plan.superseded);
      return applySweep(plan).then(function () {
        st.linesChanged = true;
        return readCart();
      }, function (error) {
        linesNotice = true;
        throw error;
      });
    }

    // One sweep, plus the price re-check a clamp needs. A clamp changes the
    // line's price (a qty-2 gift the discount did not free becomes a qty-1
    // gift that is still not free), and the engine sends itself no event to
    // look again, so it looks again here, in the same pass. Bounded: one extra
    // read and one extra plan, which cannot clamp again because every tier
    // line is now quantity 1.
    function sweepPass(cart, st) {
      var plan = planSweep(cart, cfg, snapshot(cart, cfg, today()));
      if (!plan.any) return Promise.resolve({ cart: cart, t0Swept: false });
      return applyPlan(plan, st).then(function (after) {
        if (!plan.clamped) return { cart: after, t0Swept: plan.t0Swept };
        var recheck = planSweep(after, cfg, snapshot(after, cfg, today()));
        if (!recheck.any) return { cart: after, t0Swept: plan.t0Swept };
        return applyPlan(recheck, st).then(function (after2) {
          return { cart: after2, t0Swept: plan.t0Swept || recheck.t0Swept };
        });
      });
    }

    /*
     * One reconcile pass. Resolves with its state ({error, ...}); run() uses
     * `error` to schedule the follow-up. Order:
     *   0. empty cart: re-offer (G8);
     *   1. sweepPass: the price-only sweep, every mode (G1, G4), with the
     *      post-clamp price re-check;
     *   2. T0 slot 0 (legacy state machine), then the tier slots (adds);
     *   3. FINALLY, whatever happened in 1-2: if an add was attempted or any
     *      step failed, re-read /cart.js and sweep again. This is the legacy
     *      verifyFree, moved out of the happy path so a failed bookkeeping
     *      write can never skip it and leave a charged gift behind;
     *   4. if lines changed (or an add was attempted), one marked
     *      theme:cart:change with the fresh cart plus theme:cart:reload.
     */
    function reconcile() {
      var st = { linesChanged: false, addAttempted: false, slotStateChanged: false, superseded: [], ambiguous: [], error: null };
      function fail(error) {
        if (!st.error) st.error = error || new Error('tc-gift-engine: step failed');
      }
      // A tier add that answered 5xx, counted once, from the cart as the
      // finally step read it: a tagged line for that slot and gift means the
      // add landed (free: the next pass records it; charged: planSweep spent
      // the slot's try when it removed it), so nothing more is spent here. No
      // such line means nothing landed, and the attempt spends its try now.
      function countAmbiguous(check) {
        var writes = {};
        st.ambiguous.forEach(function (a) {
          var landed = (check.items || []).some(function (item) {
            return propsOf(item)._free_gift && !isLegacyLine(item) &&
              slotKey(tierOf(item), slotOf(item)) === a.key && Number(item.variant_id) === a.variantId;
          });
          if (!landed) writes['_gift_retry_' + a.key] = String(a.tries + 1);
        });
        st.ambiguous = [];
        return Object.keys(writes).length ? setAttrs(writes) : Promise.resolve(null);
      }

      return readCart().then(function (cart) {
        notify(cart);
        // 0. FRESH-CART RE-OFFER, AJAX-aware (G8). Clears by prefix. Only a
        //    cart that was really read as empty gets here: a failed read
        //    rejects in readCart() and never clears anything.
        if (cart.items.length === 0) {
          var keys = reofferKeys(cart.attributes);
          if (cfg.reofferOnEmpty && keys.length) {
            var blank = {};
            keys.forEach(function (key) { blank[key] = ''; });
            return setAttrs(blank);
          }
          return null;
        }
        // 1. SAFETY SWEEP. The legacy engine returned after a sweep, so T0
        //    waits for the next trigger exactly as before (R1). Non-T0 slots
        //    continue, which is what makes the upgrade swap one action.
        return sweepPass(cart, st).then(function (result) {
          return afterSweep(result.cart, result.t0Swept);
        });
      }).then(null, fail).then(function () {
        // 3. FINALLY: the price check never depends on a bookkeeping write.
        //    An ambiguous (5xx) tier add is counted after it, from the same
        //    read, whether or not the sweep's own write succeeded.
        if (!st.addAttempted && !st.error) return null;
        return readCart().then(function (check) {
          var counted = function () { return countAmbiguous(check); };
          return sweepPass(check, st).then(counted, function (error) {
            fail(error);
            return counted();
          });
        });
      }).then(null, fail).then(function () {
        // 4. Publish the cart as it now is. Lines changed: the marked change,
        //    a reload for the line items, and the subscribers. Only a tier
        //    slot's bookkeeping changed (an opt-out recorded after the shopper
        //    removed a tier gift, say): the same, minus the line-item reload,
        //    so the tier card can offer the claim at once instead of after a
        //    page reload (live QA, PR #426). A T0 stamp alone publishes
        //    nothing, as before (R1): the T0 cards do not read attributes.
        //    A pending notice from a failed sweep (applyPlan) counts as
        //    moved lines, in this pass or the next one that gets here.
        var linesMoved = st.linesChanged || st.addAttempted || linesNotice;
        if (!linesMoved && !st.slotStateChanged) return null;
        return readCart().then(function (finalCart) {
          env.dispatch('theme:cart:change', { cart: finalCart, tcGiftEngine: true });
          if (linesMoved) env.dispatch('theme:cart:reload', null);
          if (linesMoved) linesNotice = false;
          notify(finalCart);
        });
      }).then(null, fail).then(function () {
        return st;
      });

      function afterSweep(cart, t0Swept) {
        var snap = snapshot(cart, cfg, today());
        var t0Step = t0Swept ? Promise.resolve(false) : t0Branch(cart, snap);
        return t0Step.then(function (t0Added) {
          if (t0Added) st.linesChanged = true;
          if (snap.mode !== MODE_TIERED) return false;
          return (t0Added ? readCart() : Promise.resolve(cart)).then(function (current) {
            return tierBranch(current);
          });
        }).then(function (tierAdded) {
          if (tierAdded) st.linesChanged = true;
        });
      }

      // T0 slot 0: the legacy state machine, branch for branch (FG:211-261).
      function t0Branch(cart, snap) {
        var t0 = snap.t0;
        if (!t0 || !t0.variantId) return Promise.resolve(false);
        var items = cart.items;
        var attrs = cart.attributes || {};
        var isGiftProduct = function (i) {
          return t0.productId != null ? i.product_id === t0.productId : i.variant_id === t0.variantId;
        };
        var hasGiftProduct = items.some(isGiftProduct);
        var qualifies = items.some(function (i) { return !isGiftProduct(i); }) && (t0.cartQualifies || t0.pageQualifies);
        var declined = attrs._gift_auto_added;
        var lastDecision = attrs[DECISION] || '';
        var lastWasAdd = lastDecision === STAMP_ADDED || lastDecision === STAMP_READDED;
        if (!hasGiftProduct && declined && qualifies && lastWasAdd) {
          return setAttrs(obj(DECISION, STAMP_REMOVED)).then(function () { return false; });
        }
        var guardSwept = lastDecision === STAMP_SWEPT;
        var retrySpent = attrs._gift_retry_used;
        if (!hasGiftProduct && qualifies && guardSwept && !retrySpent) return addT0(t0, '_gift_retry_used');
        // Self-heal (#680). Our own T0 line is in the cart but the opt-out flag
        // is empty: addT0 records the flag in a second request AFTER the add, and
        // that request was lost (a 429, or the page left in between). Left
        // empty, a later shopper removal reads as a brand-new cart and the gift
        // bounces back once. Record the flag now: once, no add, no stamp. Only
        // when it is EMPTY, so a stale "1" kept on purpose after a sweep is never
        // touched, and only for a tagged T0 slot-0 line of the gift product, so a
        // shopper buying the gift at full price never sets it. The add stays
        // first on purpose: a customer whose flag write keeps failing still gets
        // the gift. Best-effort: a failed write fails the pass (the bounded
        // follow-up writes it again) but never holds back the tier slots.
        if (!declined && items.some(function (i) { return isGiftProduct(i) && isEngineT0Line(i); })) {
          return setAttrs(obj('_gift_auto_added', '1')).then(function () { return false; }, function (error) {
            fail(error);
            return false;
          });
        }
        if (hasGiftProduct || declined || !qualifies) return Promise.resolve(false);
        return addT0(t0, '_gift_auto_added');
      }

      function addT0(t0, flagKey) {
        st.addAttempted = true;
        return api.post('/cart/add.js', { id: t0.variantId, quantity: 1, properties: t0Props() }).then(function (r) {
          var attributes = {};
          // The flag is set whatever the answer, exactly as before: a refused
          // or failed T0 add is never retried by itself (R1).
          attributes[flagKey] = '1';
          if (r && r.ok) {
            attributes[DECISION] = STAMP_ADDED;
          } else if (isRefusal(r)) {
            // G6 fix: a 4xx refusal is recorded as what it is, not "added".
            attributes[DECISION] = STAMP_NOT_ADDED;
          } else {
            // 5xx: the add may have landed. No stamp either way; the pass
            // fails so its finally-sweep and the follow-up settle it.
            fail(httpError('/cart/add.js', r));
          }
          return setAttrs(attributes).then(function () { return !!(r && r.ok); });
        });
      }

      // Every earned slot except T0 slot 0: per-slot presence, opt-out,
      // retry budget and the upgrade swap (G5, G6, G7).
      function tierBranch(cart) {
        var snap = snapshot(cart, cfg, today());
        if (snap.mode !== MODE_TIERED) return Promise.resolve(false);
        var decision = snap.decision;
        var attrs = cart.attributes || {};
        var writes = {};
        var adds = [];
        decision.earned.forEach(function (entry, index) {
          if (isLegacySlot(entry)) return;
          var key = slotKey(entry.tier, entry.slot);
          if (decision.detail.claimedBy[index]) {
            if (!attrs['_gift_added_' + key]) writes['_gift_added_' + key] = '1';
            return;
          }
          if (attrs['_gift_optout_' + key]) return;
          if (attrs['_gift_added_' + key]) {
            // We added it, nothing of ours swept it, and it is gone: the
            // customer removed it (G5). Opt out this slot only.
            writes['_gift_optout_' + key] = '1';
            writes['_gift_added_' + key] = '';
            writes[TIER_DECISION] = stampTierRemoved(cfg, entry);
            return;
          }
          var tries = parseInt(attrs['_gift_retry_' + key], 10) || 0;
          if (tries >= RETRY_LIMIT) return;
          adds.push({ entry: entry, key: key, tries: tries });
        });
        var win = decision.detail.window;
        var landed = false;
        var start = Object.keys(writes).length
          ? setAttrs(writes).then(function () { st.slotStateChanged = true; })
          : Promise.resolve();
        return adds.reduce(function (chain, add) {
          return chain.then(function () {
            st.addAttempted = true;
            return api.post('/cart/add.js', {
              id: numericId(add.entry.variant_id), quantity: 1, properties: tierProps(add.entry)
            }).then(function (r) {
              var w = {};
              var tier = findTier(win, add.entry.tier);
              if (r && r.ok) {
                landed = true;
                w['_gift_added_' + add.key] = '1';
                // Upgrade swap: an old spend-tier line was swept as charged
                // (released) in this same pass while this tier is earned.
                var swapFrom = null;
                st.superseded.forEach(function (s) {
                  if (s.tier !== add.entry.tier && s.threshold !== null) swapFrom = s;
                });
                w[TIER_DECISION] = swapFrom && add.entry.tier !== T0_KEY
                  ? stampTierSwap(cfg, add.entry, tier, swapFrom.threshold)
                  : stampTierAdded(cfg, add.entry, tier);
              } else if (isRefusal(r)) {
                // A refused add spends one try, so a gift that keeps failing is
                // attempted at most RETRY_LIMIT times per cart, not per event.
                w['_gift_retry_' + add.key] = String(add.tries + 1);
                w[TIER_DECISION] = stampTierNotAdded(cfg, add.entry);
              } else {
                // 5xx: the add may have landed. No stamp, and no retry spent
                // YET: the finally step looks at the cart and counts this one
                // attempt exactly once (countAmbiguous). Spending it here as
                // well as in the sweep, when the line had landed charged, cost
                // two tries for one attempt and the one-shot restore never ran
                // (Codex connector review on f57ce7c). The pass fails, so its
                // finally-sweep and the follow-up settle the line.
                st.ambiguous.push({ key: add.key, tries: add.tries, variantId: numericId(add.entry.variant_id) });
                fail(httpError('/cart/add.js', r));
              }
              return Object.keys(w).length ? setAttrs(w) : null;
            });
          });
        }, start).then(function () { return landed; });
      }
    }

    return {
      run: run,
      schedule: schedule,
      isSettled: isSettled,
      notify: notify,
      subscribe: function (fn) { subscribers.push(fn); },
      snapshot: function (cart) { return snapshot(cart, cfg, today()); },
      // The legacy T0 claim card's gate (#479 item 2), for the inline card
      // script in snippets/free-gift-cart-cards.liquid.
      offersLegacyT0: function (cart, variantId) { return legacyT0Offered(snapshot(cart, cfg, today()), variantId); },
      // The active window's event T0 gift for this cart, or null (#765).
      eventT0Target: function (cart) { return eventT0Target(snapshot(cart, cfg, today()), cfg); },
      // Will the engine add the T0 gift itself, on a run that is in flight or
      // scheduled? (#765.) False once the engine has settled, even if the add
      // is still due: nothing will act on it until the next cart event.
      t0AddPending: function (cart) { return !isSettled() && t0AddDue(cart, snapshot(cart, cfg, today())); },
      whenSettled: whenSettled,
      isBusy: function () { return busy; },
      runCount: function () { return runs; },
      cfg: cfg,
      today: today
    };
  }

  /* ======================================================================
   * 5. UI
   * ====================================================================== */

  // Customer-facing copy. Drafted, then passed through the humanizer skill
  // (embedded mode). Status sentences reuse the live cart-card copy verbatim.
  // Deal Days (#480): the progress bar and PDP card lines are Kevin's issue
  // copy, checked against exports/advertising-language/advertising-language-guide.md
  // (F1: every condition next to "free"; A2: no bare "up to"; G1/G2: no
  // "Prime" or internal names; H1: no em-dashes). Tier gifts carry no dollar
  // value anywhere (Kevin, #480); the T0 cubes copy is Liquid and unchanged.
  // The PDP gift ladder and callout wording (2026-11-01) is Kevin's pick
  // ("Step amounts only"), written in the voice of the live BFCM terms and
  // checked against the advertising guide (F1: what qualifies, one per order,
  // while supplies last, next to "free"). Thresholds only: no "Add $X more"
  // and no dollar value for any step gift on the PDP.
  // The ladder names its gifts by kind (ladderNoun): "bag" only when every
  // shown gift is a bag (BFCM: Sling, Breenon, Weekender), "gift" otherwise,
  // "gifts" when a shown step gives more than one gift (a multi-slot step,
  // where "one gift" would be untrue, PR #607 review).
  var COPY = {
    progressFirst: 'Add {amount} more to get a free {gift}, while supplies last.',
    progressUpgrade: 'Add {amount} more to upgrade your free {current} to the {gift}, while supplies last.',
    progressTop: "You've unlocked a free {gift}.",
    pdpAlso: 'Also free with your luggage: {gift}',
    ladderHeading: {
      bag: 'Plus one free bag by order total',
      gift: 'Plus one free gift by order total',
      gifts: 'Plus free gifts by order total'
    },
    ladderStep: '{threshold} or more: {gift}',
    ladderCurrent: 'Your order qualifies. Added to your cart at {zero}.',
    ladderAhead: 'At {threshold} or more, you get {item}.',
    ladderAheadInstead: 'At {threshold} or more, you get {item} instead.',
    ladderItem: { bag: 'this bag', gift: 'this gift', gifts: 'these gifts' },
    ladderPassed: 'Your order reaches a higher step, so you get the {noun} below instead.',
    ladderFootnote: {
      bag: 'One bag per order, for the highest step your order reaches. While supplies last.',
      gift: 'One gift per order, for the highest step your order reaches. While supplies last.',
      gifts: 'Gifts from one step per order, for the highest step your order reaches. While supplies last.'
    },
    ladderOfferLink: 'Offer details',
    ladderOfferPath: 'pages/promotions-faqs',
    ladderGiftJoin: ' + ',
    // The T0 callout's limit line. The Liquid default is the evergreen cubes
    // terms ("Limit one free 3-piece packing cube set per household"); a
    // window whose T0 is a different, function-priced gift (the BFCM kit) is
    // one per order (BFCM terms), set by retitleCallout.
    calloutLimitOrder: 'One per order, while supplies last.',
    tierWon: 'Free with orders of {threshold} or more',
    tierClaim: 'Your order qualifies for a free {gift}',
    tierClaimButton: 'Add free gift',
    // T0 only (retitleCallout, a BFCM kit on the Liquid-rendered T0 callout).
    regularPrice: 'Regular price {value},',
    free: 'FREE',
    msgCharged: 'This gift is not free right now, so we are taking it back out of your cart.',
    msgFailed: 'We could not add the gift just now. Please try again.',
    msgGone: 'Your cart no longer qualifies for this gift.',
    msgAlready: 'This item is already in your cart.'
  };
  function fill(template, values) {
    return template.replace(/\{(\w+)\}/g, function (all, key) {
      return hasOwn(values, key) ? values[key] : all;
    });
  }

  /*
   * One spend tier as the UI sees it. A tier with several slots grants EVERY
   * slot that resolves (decide and the Function earn them all), so:
   *   gifts        every resolved slot's gift, in slot order, with its pool
   *                entry and whether it can actually be given (a valid entry
   *                that is available; stock is a display concern only)
   *   usableGifts  the ones that can be given; the ladder shows only these
   *   gid          the first resolved slot's gift (null when every slot is
   *                retired: tierHasGift is false); the first entry the
   *                decision core earns for this tier
   *   title, image, imageLarge  of the first usable gift (the progress
   *                sentence and the row thumbnail name and show that one)
   *   usable       any slot usable (Codex P2 round 2 on PR #607: a later
   *                usable slot keeps the step even if the first is sold out)
   */
  function tierInfo(tier, decision, cfg) {
    var gifts = [];
    tier.slots.forEach(function (slot) {
      var gid = resolveSlotGift(slot, decision.detail.today, decision.detail.ctx);
      if (gid === null) return;
      var entry = poolEntry(cfg, gid);
      var gift = {
        gid: gid,
        title: entry ? entry.title : null,
        image: entry ? (entry.image || null) : null,
        imageLarge: entry ? (entry.imageLarge || entry.image || null) : null,
        available: !!(entry && entry.available)
      };
      gift.usable = !!(gift.title && gift.available);
      gifts.push(gift);
    });
    var usableGifts = gifts.filter(function (gift) { return gift.usable; });
    var lead = usableGifts[0] || gifts[0] || null;
    return {
      tier: tier,
      gid: gifts.length ? gifts[0].gid : null,
      gifts: gifts,
      usableGifts: usableGifts,
      title: lead ? lead.title : null,
      image: lead ? lead.image : null,
      imageLarge: lead ? lead.imageLarge : null,
      available: usableGifts.length > 0,
      usable: usableGifts.length > 0
    };
  }

  /*
   * The gift ladder (#604): every spend tier (non-t0) of the active window,
   * lowest threshold first, each with its resolved gift (tierInfo, so
   * candidates, dated phases and retirements work exactly as for the cards).
   * Driven entirely by the config and the gift pool: a new gift product needs
   * no code change. Each step's state matches what the discount gives
   * (design section 0, R1: T0 plus only the highest earned spend tier):
   *   current  the highest EARNED tier, only when its gift is usable. A
   *            fully retired step (no slot resolves: every candidate in
   *            state.retired) is skipped as if absent, exactly as in
   *            earnedTiers (#609), so the step below becomes current. An
   *            earned tier whose gift is merely sold out (stock, not retired)
   *            is NOT replaced by a lower tier's gift: the discount still
   *            earns it and frees only its gift, so "your free Travel Pillow"
   *            would be untrue at $260 (Codex connector review on f57ce7c).
   *   passed   a usable tier at or below the current tier that is not
   *            current (the bigger step supersedes it). Shown ONLY when a
   *            current step exists; with none, these rows are left out.
   *   ahead    not reached yet; `remaining` is what is still to spend.
   * A tier with no usable gift (every candidate unavailable, or no pool
   * entry) is left out entirely: never promise what cannot be given. Ties go
   * to the first listed, matching earnedTiers: a later tier tied at the same
   * threshold can never be selected, so it is left out. Any number of steps. The PDP
   * card and the cart marks both render exactly these steps.
   *
   * extraCents (the PDP) counts a product that is not in the cart yet, at its
   * selected price, on top of the cart's pre-discount non-gift spend.
   * null: not tiered, or no active window.
   */
  function ladderView(snap, cfg, extraCents) {
    var decision = snap.decision;
    var win = decision.detail && decision.detail.window;
    if (decision.mode !== MODE_TIERED || !win || !decision.detail.ctx) return null;
    var spend = decision.detail.spendCents + Math.max(0, Math.round(Number(extraCents) || 0));
    var earned = null;
    var infos = [];
    var thresholdTaken = {};
    win.tiers.forEach(function (tier, order) {
      if (tier.key === T0_KEY) return;
      var info = tierInfo(tier, decision, cfg);
      // Fully retired (no slot resolves): not a step at all, never earned
      // (earnedTiers skips it the same way), never shown.
      if (info.gid === null) return;
      // Tied thresholds: earnedTiers can only ever select the FIRST listed
      // tier with a gift at a threshold, so a later tie is not a step either.
      // Leaving it out keeps it from being labelled "passed" (it was never
      // eligible) and from hiding anything (Codex P2 on PR #607). The
      // gift-config tool refuses ties; this is defence in depth.
      if (hasOwn(thresholdTaken, tier.threshold_cents)) return;
      thresholdTaken[tier.threshold_cents] = true;
      infos.push({ info: info, order: order });
      if (tier.threshold_cents <= spend && (!earned || tier.threshold_cents > earned.tier.threshold_cents)) earned = info;
    });
    var current = earned && earned.usable ? earned : null;
    var steps = infos.filter(function (row) {
      if (!row.info.usable) return false;
      // Passed rows only exist beside a current row. With no current row
      // (the highest earned tier's gift is unusable, so the order gets no
      // step gift at all), "Swapped for the bigger gift below." would be
      // untrue: nothing below is the shopper's. Those rows are left out and
      // only the steps still ahead remain, in the no-current wording; no
      // rows left means no ladder (PDP card hidden, no cart marks).
      var passedShown = !!current || row.info.tier.threshold_cents > spend;
      return passedShown;
    }).sort(function (a, b) {
      return a.info.tier.threshold_cents - b.info.tier.threshold_cents || a.order - b.order;
    }).map(function (row) {
      var threshold = row.info.tier.threshold_cents;
      var reached = threshold <= spend;
      return {
        key: row.info.tier.key,
        info: row.info,
        threshold: threshold,
        reached: reached,
        state: row.info === current ? 'current' : (reached ? 'passed' : 'ahead'),
        remaining: reached ? 0 : threshold - spend
      };
    });
    return {
      spend: spend,
      steps: steps,
      current: current,
      top: steps.length ? steps[steps.length - 1].threshold : 0,
      qualifies: decision.detail.qualifies,
      window: win
    };
  }

  // The spend tier currently given (`current`, see ladderView) and the next
  // usable one above the spend (`next`), read off the ladder so the progress
  // sentence and the ladder can never disagree. With no usable current tier,
  // the sentence falls back to the next tier's own wording, or to no line.
  function spendTiers(snap, cfg, extraCents) {
    var ladder = ladderView(snap, cfg, extraCents);
    if (!ladder) return null;
    var next = null;
    ladder.steps.forEach(function (step) {
      if (!next && step.state === 'ahead') next = step.info;
    });
    return { spend: ladder.spend, current: ladder.current, next: next, qualifies: ladder.qualifies, window: ladder.window, ladder: ladder };
  }

  /*
   * The cart progress bar (#480): what it shows and what it says.
   *   text    the sentence on the bar
   *   spoken  whether the polite live line says it. On the cart page the
   *           "unlocked" state is shown but NOT spoken: the won card (or the
   *           claim card) right there already says it, and its announcer
   *           speaks the change once (NVDA rounds 1-2 on PR #426). The drawer
   *           has no tier card, so it says it.
   *   ratio   bar fill, spend over the TOP ladder step's threshold (#604),
   *           so the whole ladder fits on one bar
   *   marks   one per ladder step (ladderView): its position on the bar
   *           (threshold over the top threshold), whether the spend has
   *           reached it, and its threshold label. Painted aria-hidden.
   *   steps   the same steps in words (ladderCopy: heading, one row per step
   *           with its state, footnote) for an off-screen list beside the
   *           marks, so assistive technology gets every step and not only
   *           the next one the sentence names (Codex on PR #607, 1.3.1)
   *   image   the gift's thumbnail
   * null: no bar (legacy mode, no qualifying item, no usable tier).
   * The sentence (text, spoken, image) is unchanged by #604.
   */
  function progressView(snap, cfg, currency, surface) {
    var tiers = spendTiers(snap, cfg);
    if (!tiers || !tiers.qualifies) return null;
    var view = null;
    if (tiers.next) {
      var amount = formatMoney(tiers.next.tier.threshold_cents - tiers.spend, currency);
      var text = tiers.current
        ? fill(COPY.progressUpgrade, { amount: amount, current: displayName(tiers.current.title, true), gift: displayName(tiers.next.title, true) })
        : fill(COPY.progressFirst, { amount: amount, gift: displayName(tiers.next.title, true) });
      view = { text: text, spoken: true, image: tiers.next.image };
    } else if (tiers.current) {
      view = {
        text: fill(COPY.progressTop, { gift: displayName(tiers.current.title, true) }),
        spoken: surface !== 'cart',
        image: tiers.current.image
      };
    }
    if (!view) return null;
    var ladder = tiers.ladder;
    var top = ladder.top;
    view.ratio = top > 0 ? Math.max(0, Math.min(1, tiers.spend / top)) : 1;
    view.marks = ladder.steps.map(function (step) {
      return {
        key: step.key,
        position: top > 0 ? Math.max(0, Math.min(1, step.threshold / top)) : 1,
        reached: step.reached,
        label: formatMoney(step.threshold, currency)
      };
    });
    view.steps = ladderCopy(ladder, currency);
    return view;
  }
  // The sentence the live line speaks ('' = silent).
  function progressText(snap, cfg, currency, surface) {
    var view = progressView(snap, cfg, currency, surface);
    return view && view.spoken ? view.text : '';
  }

  // The progress line (cart page + drawer). One element, updated only after
  // the cart settles (800 ms quiet) and only when the sentence changes, so a
  // screen reader hears it once per settled cart, not per quantity click. It
  // starts aria-live="off" and turns polite after its first fill, so a page
  // load never announces it. It also waits for the engine to settle (no run
  // in flight or scheduled), so in a tier swap the new card is in place
  // before the line talks about it (NVDA capture on PR #426: the line spoke
  // about two seconds before the swapped card appeared). The wait is capped.
  // getView returns a sentence, or a progressView; paint(view), if given,
  // draws the bar around the line (#480) on the same settled schedule.
  function bindProgress(el, getView, isSettled, paint) {
    var timer = null;
    var initialized = false;
    var waited = 0;
    var SETTLE_POLL_MS = 250;
    var SETTLE_MAX_MS = 6000;
    function apply() {
      if (initialized && isSettled && !isSettled() && waited < SETTLE_MAX_MS) {
        waited += SETTLE_POLL_MS;
        timer = setTimeout(apply, SETTLE_POLL_MS);
        return;
      }
      waited = 0;
      var view = getView();
      var text = typeof view === 'string' ? view : (view && view.spoken ? view.text : '');
      if (el.textContent !== text) el.textContent = text;
      if (text) {
        el.removeAttribute('data-empty');
      } else {
        el.setAttribute('data-empty', '');
      }
      if (paint) paint(typeof view === 'string' ? (view ? { text: view, spoken: true, ratio: 0, image: null } : null) : view);
      if (!initialized) {
        initialized = true;
        setTimeout(function () { el.setAttribute('aria-live', 'polite'); }, 150);
      }
    }
    return function schedule() {
      if (timer) clearTimeout(timer);
      waited = 0;
      timer = setTimeout(apply, initialized ? 800 : 0);
    };
  }

  // A percentage for an inline style, two decimals at most.
  function pct(fraction) {
    return String(Math.round(Math.max(0, Math.min(1, Number(fraction) || 0)) * 10000) / 100) + '%';
  }

  /*
   * The ladder marks under the progress bar (#604): one dot per step on the
   * track, filled once the spend reaches it, with the threshold under it.
   * Built here from the live view, never server-rendered (no empty shell),
   * inside one aria-hidden row placed right after the track. Dot and label
   * sit at left: p% with translateX(-p%), so the first and last stay inside
   * the track at any width (no horizontal scroll at 320px). Rebuilt only
   * when the marks change.
   *
   * Right after that row, an off-screen list (.visually-hidden) says the
   * same steps in words: the PDP ladder's heading, one item per step with its
   * state ("$250+ order: Everyday Sling. Add $70 more to get it instead.")
   * and its footnote. The dots alone show every threshold to sighted shoppers,
   * so screen readers need them too (1.3.1); the live sentence still names
   * only the next gift. NOT a live region: it is read when reached, never
   * announced on change. Rebuilt only when its words change.
   */
  var progressStepsSeq = 0;
  function paintProgressSteps(row, steps) {
    var holder = row.nextSibling && row.nextSibling.hasAttribute && row.nextSibling.hasAttribute('data-fg-progress-steps')
      ? row.nextSibling
      : null;
    if (!steps || !steps.rows || !steps.rows.length) {
      if (holder) holder.setAttribute('hidden', '');
      return;
    }
    if (!holder) {
      holder = el('div', { 'class': 'visually-hidden', 'data-fg-progress-steps': '' });
      row.parentNode.insertBefore(holder, row.nextSibling);
    }
    var items = steps.rows.map(function (r) { return r.title + '. ' + r.status; });
    var key = [steps.heading].concat(items, [steps.footnote]).join('\n');
    if (holder.getAttribute('data-fg-steps') !== key) {
      progressStepsSeq += 1;
      var headingId = 'fg-progress-steps-h-' + progressStepsSeq;
      holder.textContent = '';
      holder.appendChild(el('p', { id: headingId, text: steps.heading }));
      holder.appendChild(el('ol', { role: 'list', 'aria-labelledby': headingId },
        items.map(function (text) { return el('li', { text: text }); })));
      holder.appendChild(el('p', { text: steps.footnote }));
      holder.setAttribute('data-fg-steps', key);
    }
    holder.removeAttribute('hidden');
  }

  function paintProgressMarks(box, fillEl, marks, steps) {
    var row = box.querySelector('[data-fg-progress-marks]');
    var list = Array.isArray(marks) ? marks : [];
    if (!list.length) {
      if (row) {
        row.setAttribute('hidden', '');
        paintProgressSteps(row, null);
      }
      return;
    }
    var track = fillEl && fillEl.parentNode;
    if (!row) {
      if (!track || !track.parentNode) return;
      row = el('span', { 'class': 'fg-progress__marks', 'data-fg-progress-marks': '', 'aria-hidden': 'true' });
      track.parentNode.insertBefore(row, track.nextSibling);
    }
    paintProgressSteps(row, steps);
    var key = list.map(function (m) { return [m.key, m.position, m.reached ? 1 : 0, m.label].join(':'); }).join('|');
    if (row.getAttribute('data-fg-marks') !== key) {
      row.textContent = '';
      list.forEach(function (m) {
        var at = 'left:' + pct(m.position) + '; transform:translateX(-' + pct(m.position) + ');';
        var dot = el('span', { 'class': 'fg-progress__mark', 'data-fg-progress-mark': m.key, style: at });
        if (m.reached) dot.setAttribute('data-reached', '');
        row.appendChild(dot);
        row.appendChild(el('span', { 'class': 'fg-progress__label', style: at, text: m.label }));
      });
      row.setAttribute('data-fg-marks', key);
    }
    row.removeAttribute('hidden');
  }

  // Draws the bar around the live line (snippets/free-gift-progress.liquid).
  // Everything here is aria-hidden except the off-screen step list: the live
  // line carries the change, the list every step.
  function paintProgressBox(box, view) {
    if (!box) return;
    var quiet = box.querySelector('[data-fg-progress-quiet]');
    var fillEl = box.querySelector('[data-fg-progress-fill]');
    var thumb = box.querySelector('[data-fg-progress-thumb]');
    if (!view) {
      box.setAttribute('data-empty', '');
      if (quiet) { quiet.textContent = ''; quiet.setAttribute('hidden', ''); }
      if (fillEl) fillEl.style.width = '0%';
      if (thumb) thumb.setAttribute('hidden', '');
      paintProgressMarks(box, fillEl, null);
      return;
    }
    paintProgressMarks(box, fillEl, view.marks, view.steps);
    box.removeAttribute('data-empty');
    if (quiet) {
      if (view.spoken) {
        quiet.textContent = '';
        quiet.setAttribute('hidden', '');
      } else {
        if (quiet.textContent !== view.text) quiet.textContent = view.text;
        quiet.removeAttribute('hidden');
      }
    }
    // Floor, so a cart one cent short never draws a full bar.
    if (fillEl) fillEl.style.width = Math.floor((view.ratio || 0) * 100) + '%';
    if (view.image) {
      if (!thumb) {
        thumb = el('img', { 'class': 'fg-progress__thumb', 'data-fg-progress-thumb': '', alt: '', width: '44', height: '44' });
        box.insertBefore(thumb, box.firstChild);
      }
      if (thumb.getAttribute('src') !== view.image) thumb.setAttribute('src', view.image);
      thumb.removeAttribute('hidden');
    } else if (thumb) {
      thumb.setAttribute('hidden', '');
    }
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (key) {
      if (key === 'text') node.textContent = attrs[key];
      else if (key === 'style') node.setAttribute('style', attrs[key]);
      else node.setAttribute(key, attrs[key]);
    });
    (children || []).forEach(function (child) { if (child) node.appendChild(child); });
    return node;
  }
  var SR_ONLY = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';

  /*
   * Tier cards on the cart page: one group per earned slot other than T0
   * slot 0, each with the T0 cards' three states (won, claim, none) and the
   * #348 claim safety chain, per slot (G11 to G14). Built client-side because
   * the active window depends on the store-local date.
   *
   * How a screen reader hears them (NVDA capture on PR #426; WCAG 4.1.3 and
   * 2.4.3). Every change is spoken ONCE, through ONE channel:
   *   - The cards are NOT in a live region, so nothing that takes focus can
   *     also be read out by a region (the doubled "no longer qualifies").
   *   - A change the shopper did not cause (the engine adds or swaps a gift,
   *     a removal turns the card into a claim) is spoken by one visually
   *     hidden polite announcer, as one sentence.
   *   - The result of the shopper's own "Add free gift" is spoken by moving
   *     focus: to the won card's message when the gift lands, or to the
   *     status message when it cannot. The announcer stays silent then.
   *   - The won card's focus target is a plain paragraph whose only readable
   *     text is the same one-sentence string the announcer uses (the visual
   *     lines are aria-hidden). No group name repeats it, and no visually
   *     hidden "." sits alone, so it is read once, without "dot" (NVDA
   *     round 2).
   *   - Nothing about the claim button's state changes while its claim runs:
   *     `disabled` drops focus to <body>, and even aria-disabled on the
   *     focused button made NVDA say "unavailable" (round 2). A busy guard in
   *     the script stops repeat clicks; the busy look is a data attribute
   *     (CSS only, not exposed to assistive technology).
   */
  function bindTierCards(root, engine, api) {
    var cfg = engine.cfg;
    var groups = {};
    var first = true;
    var SWEEP_POLL_MS = 800;
    var SWEEP_TRIES = 3;

    // The container is not live (an older render could still carry the
    // attribute); the announcer is the only live region here.
    root.removeAttribute('aria-live');
    var announcer = el('p', { 'class': 'fg-tier-live', 'data-fg-tier-live': '', 'aria-live': 'polite', style: SR_ONLY });
    root.appendChild(announcer);
    var announceTimer = null;
    function say(text) {
      if (!text) return;
      if (announceTimer) clearTimeout(announceTimer);
      // Empty first, then write, so the same sentence twice in a row is still
      // a change a screen reader announces.
      announcer.textContent = '';
      announceTimer = setTimeout(function () { announcer.textContent = text; }, 60);
    }
    function focusEl(node) {
      try { node.focus({ preventScroll: true }); } catch (e) { node.focus(); }
    }

    function readCart() { return api.readCart().then(requireCart); }

    function slotView(group, cart) {
      var snap = engine.snapshot(cart);
      var decision = snap.decision;
      var result = { earned: false, entry: null, line: null, free: false, snap: snap };
      if (decision.mode !== MODE_TIERED) return result;
      decision.earned.forEach(function (entry, index) {
        if (slotKey(entry.tier, entry.slot) !== group.key || isLegacySlot(entry)) return;
        result.earned = true;
        result.entry = entry;
        var lineId = decision.detail.claimedBy[index];
        if (lineId) {
          (cart.items || []).forEach(function (item) { if (item.key === lineId) result.line = item; });
        }
      });
      if (!result.line) {
        // A tagged line for this slot that no earned slot claims still decides
        // the state: charged (superseded, about to be swept) shows nothing,
        // free (the Function sees a qualifier this page does not) is won.
        // Never offer a claim over either.
        (cart.items || []).forEach(function (item) {
          if (!result.line && propsOf(item)._free_gift && !isLegacyLine(item) && slotKey(tierOf(item), slotOf(item)) === group.key) {
            result.line = item;
            result.stale = true;
          }
        });
      }
      // Price decides, not the theme's frozen view of qualification: a slot
      // line the discount is freeing is won even if this page cannot confirm
      // the slot is earned (the engine never removes a free line either).
      result.free = !!result.line && isFreeLine(result.line);
      return result;
    }

    function stateFor(group, cart) {
      var view = slotView(group, cart);
      if (view.line) return view.free ? 'won' : 'none';
      if (!view.earned) return 'none';
      var attrs = cart.attributes || {};
      var tries = parseInt(attrs['_gift_retry_' + group.key], 10) || 0;
      var engineWontAdd = !!attrs['_gift_optout_' + group.key] || tries >= RETRY_LIMIT;
      var entry = poolEntry(cfg, view.entry.variant_id);
      if (engineWontAdd && entry && entry.available) return 'claim';
      return 'none';
    }

    function taggedChargedGift(cart) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (propsOf(items[i])._free_gift && !isFreeLine(items[i])) return items[i];
      }
      return null;
    }

    function buildGroup(key, entry, cart) {
      var pool = poolEntry(cfg, entry.variant_id) || {};
      var win = engine.snapshot(cart).decision.detail.window;
      var tier = findTier(win, entry.tier);
      var currency = (cart && cart.currency) || 'USD';
      var heading = fill(COPY.tierWon, { threshold: formatMoney(tier ? tier.threshold_cents : 0, currency) });
      var name = displayName(pool.title, false); // the product-name label keeps its full title
      var claimHeading = fill(COPY.tierClaim, { gift: displayName(pool.title, true) });
      var id = 'fg-tier-' + key;
      var cardBase = 'align-items:center; gap:14px; padding:14px 16px; border-radius:10px; margin-top:10px;';
      var img = function () {
        return pool.image ? el('img', { src: pool.image, alt: '', width: '56', height: '56', style: 'width:56px; height:56px; object-fit:contain; border-radius:6px; flex:0 0 auto;' }) : null;
      };
      // No dollar value on a tier gift, in the text, the speech or the names
      // (Kevin, #480). One sentence, one text node: what the won card reads on
      // focus and what the announcer says ("Free with orders of $150 or more.
      // The Travel Pillow.").
      var wonSpeech = heading + '. ' + name + '.';
      // Won card. Its focus target (after a successful claim) is the message
      // paragraph, not the card: no role, no group name, so nothing is read
      // twice. The paragraph's readable text is wonSpeech (visually hidden,
      // punctuation inside the same text node); the visual lines are
      // aria-hidden.
      var wonText = el('p', { 'data-fg-tier-won-text': key, tabindex: '-1', style: 'flex:1 1 auto; margin:0;' }, [
        el('span', { style: SR_ONLY, text: wonSpeech }),
        el('span', { 'aria-hidden': 'true', style: 'display:block; font-weight:700; color:#0E3D2D;', text: heading }),
        el('span', { 'aria-hidden': 'true', style: 'display:block; margin-top:2px; font-size:14px; color:#41544b;' }, [
          document.createTextNode(name + ' · '),
          el('span', { style: 'color:#BF3D1E; font-weight:700;', text: COPY.free })
        ])
      ]);
      var won = el('div', { 'class': 'fg-card fg-card--won', 'data-fg-tier-card': 'won', hidden: '', style: 'display:none; ' + cardBase + ' border:1px solid #0E3D2D; background:#f4f8f6;' }, [
        img(),
        wonText
      ]);
      var btn = el('button', { type: 'button', 'class': 'btn', 'data-fg-tier-claim': key, style: 'padding:9px 18px; min-height:44px; flex:0 0 auto;', text: COPY.tierClaimButton });
      // Claim card: a group labelled by its heading, which NVDA reads as
      // context when Tab lands on the button ("Your order qualifies for a free
      // Travel Pillow, grouping, Add free gift, button"). The card itself never
      // takes focus (that would read the label and then the same text again);
      // when focus has to move onto it, it goes to the button.
      var claim = el('div', { 'class': 'fg-card fg-card--claim', 'data-fg-tier-card': 'claim', role: 'group', 'aria-labelledby': id + '-claim-h', hidden: '', style: 'display:none; flex-wrap:wrap; ' + cardBase + ' border:1px dashed #0E3D2D; background:#fff;' }, [
        img(),
        el('div', { style: 'flex:1 1 200px;' }, [
          el('p', { id: id + '-claim-h', style: 'margin:0; font-weight:700; color:#0E3D2D;', text: claimHeading })
        ]),
        btn
      ]);
      // Status line for the endings of the shopper's own click. Not in any
      // live region: it is spoken once, when focus lands on it.
      var status = el('p', { 'data-fg-tier-status': key, tabindex: '-1', hidden: '', style: 'display:none; margin:8px 0 0; padding:10px 12px; border:1px solid #0E3D2D; border-radius:8px; background:#f4f8f6; font-size:14px; line-height:1.4; color:#0E3D2D;' });
      var wrap = el('div', { 'class': 'fg-tier', 'data-fg-slot': key, 'data-fg-state': 'none', 'data-fg-variant': String(numericId(entry.variant_id)) }, [won, claim, status]);
      var group = {
        key: key, wrap: wrap, won: won, wonText: wonText, claim: claim, btn: btn, status: status, adding: false, clearedBy: null, variant: entry.variant_id,
        // What the announcer says for a change the shopper did not cause.
        speech: {
          won: wonSpeech,
          claim: claimHeading + '. ' + COPY.tierClaimButton
        }
      };
      btn.addEventListener('click', function () { onClaim(group); });
      return group;
    }

    function setVisible(card, visible) {
      card.style.display = visible ? 'flex' : 'none';
      if (visible) card.removeAttribute('hidden'); else card.setAttribute('hidden', '');
    }
    // Where focus goes when it has to move onto a card: the won card's
    // message, or the claim button (never the claim group itself).
    function focusTarget(group, state) {
      return state === 'won' ? group.wonText : group.btn;
    }
    // A claim in flight: the script ignores further clicks; the button only
    // LOOKS busy (a data attribute for the CSS). No ARIA state changes on the
    // focused button, so a screen reader says nothing until the result.
    function setAdding(group, adding) {
      group.adding = adding;
      if (adding) group.btn.setAttribute('data-fg-busy', '');
      else group.btn.removeAttribute('data-fg-busy');
    }
    function setStatus(group, text, clearedBy) {
      group.clearedBy = text ? (clearedBy || null) : null;
      if (text) {
        group.status.textContent = text;
        group.status.style.display = 'block';
        group.status.removeAttribute('hidden');
      } else {
        if (!group.status.textContent && group.status.hasAttribute('hidden')) return;
        group.status.textContent = '';
        group.status.style.display = 'none';
        group.status.setAttribute('hidden', '');
      }
    }
    // An ending of the shopper's own click: write the status and move focus
    // to it. Spoken once, by focus (the status is in no live region).
    function announce(group, text, clearedBy) {
      setStatus(group, text, clearedBy);
      focusEl(group.status);
    }

    /*
     * render(group, state, opts)
     *   opts.byShopper: this change is the result of the shopper's own click,
     *   so it is spoken through focus, never through the announcer.
     */
    function render(group, state, opts) {
      var o = opts || {};
      // A claim of the shopper's own is in flight: the claim card stays as it
      // is (button busy, focus on it). A cart event that lands meanwhile can
      // read as "none" (the claim has already cleared the opt-out), and hiding
      // the card then would drop focus to <body> with nothing said (Codex
      // round 5). The claim's own ending clears `adding` first, then decides
      // the next state and where focus goes.
      if (group.adding) return;
      var previous = group.wrap.getAttribute('data-fg-state');
      if (previous === state) return;
      group.wrap.setAttribute('data-fg-state', state);
      var clearing = !!(group.clearedBy && group.clearedBy.indexOf(state) !== -1);
      var onStatus = clearing && group.status.contains(document.activeElement);
      // Going to "none" leaves nothing else to hold focus, so a message the
      // shopper is on stays (hiding it would drop focus to <body>); from
      // "won" or "claim" the new card takes focus instead (below).
      if (clearing && !(onStatus && state === 'none')) setStatus(group, '');
      var focusInside = group.won.contains(document.activeElement) || group.claim.contains(document.activeElement);
      if (state === 'none') {
        if (focusInside && !o.byShopper) {
          // The card the shopper is standing on goes away under them: say why
          // and keep focus on something real (focus is never dropped).
          announce(group, COPY.msgGone, ['won', 'claim']);
        }
        setVisible(group.won, false);
        setVisible(group.claim, false);
        return;
      }
      var show = state === 'won' ? group.won : group.claim;
      var hide = state === 'won' ? group.claim : group.won;
      setVisible(show, true);
      var stranded = onStatus || hide.contains(document.activeElement);
      // Focus moves before the old card hides, so it never passes through
      // <body> on the way.
      if (stranded) focusEl(focusTarget(group, state));
      setVisible(hide, false);
      // Spoken once: by focus when focus moved, otherwise by the announcer,
      // and never on the first render (the page as loaded) or for the ending
      // of the shopper's own claim.
      if (!stranded && !o.byShopper && !first) say(group.speech[state]);
    }

    // The ending of a successful claim: the won card takes focus when the
    // shopper is still on the claim card (spoken once, by focus); if they
    // have moved on, the announcer says it once instead.
    function claimWon(group) {
      var onCard = group.wrap.contains(document.activeElement);
      render(group, 'won', { byShopper: onCard });
      if (onCard && !group.wonText.contains(document.activeElement)) focusEl(group.wonText);
    }

    function wake(cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    }
    function chaseCart(triesLeft, settle) {
      return new Promise(function (resolve) { setTimeout(resolve, SWEEP_POLL_MS); })
        .then(readCart)
        .then(function (cart) {
          if (settle(cart)) return;
          if (triesLeft > 1) return chaseCart(triesLeft - 1, settle);
          window.location.reload();
        });
    }
    function sweepChargedGift(cartHint) {
      var source = cartHint && cartHint.items ? Promise.resolve(cartHint) : readCart();
      return source.then(function (cart) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
        return chaseCart(SWEEP_TRIES, function (after) {
          if (taggedChargedGift(after)) return false;
          wake(after);
          return true;
        });
      }).catch(function () { window.location.reload(); });
    }
    function chargedEnding(group, cart, ours) {
      if (!ours && !taggedChargedGift(cart)) { announce(group, COPY.msgAlready, ['won', 'claim']); return; }
      announce(group, COPY.msgCharged, ['won']);
      sweepChargedGift(cart);
    }
    // A click ending with no card left: message (and focus) first, then the
    // cards go, so focus moves straight from the button to the message.
    function endWithout(group, text, clearedBy) {
      announce(group, text, clearedBy);
      render(group, 'none', { byShopper: true });
    }

    // The #348 claim safety chain, per slot: pre-read, attribute write,
    // re-read, add with the slot's tags, HTTP status first, confirm the price,
    // bounded chase on an ambiguous rejection.
    function onClaim(group) {
      if (group.adding) return;
      setAdding(group, true);
      setStatus(group, '');
      var confirmedCart = null;
      var addLanded = false;
      var addAttempted = false;
      readCart().then(function (cart) {
        var view = slotView(group, cart);
        if (view.line) {
          setAdding(group, false);
          if (view.free) { claimWon(group); wake(cart); return null; }
          chargedEnding(group, cart, false);
          render(group, 'none', { byShopper: true });
          return null;
        }
        if (!view.earned) {
          setAdding(group, false);
          endWithout(group, COPY.msgGone, ['won', 'claim']);
          return null;
        }
        var attributes = {};
        attributes['_gift_optout_' + group.key] = '';
        attributes['_gift_retry_' + group.key] = '';
        attributes['_gift_added_' + group.key] = '1';
        attributes[TIER_DECISION] = stampTierReadded(cfg, view.entry);
        // The bookkeeping write is checked like every other cart write: if it
        // fails, nothing has been added yet, so the catch below hands the
        // button back with "please try again".
        return api.post('/cart/update.js', { attributes: attributes }).then(requireOk('/cart/update.js')).then(function () {
          return readCart();
        }).then(function (fresh) {
          var now = slotView(group, fresh);
          if (now.line) { confirmedCart = fresh; return now.free ? 'won' : 'charged'; }
          if (!now.earned) return 'none';
          addAttempted = true;
          return api.post('/cart/add.js', { id: numericId(now.entry.variant_id), quantity: 1, properties: tierProps(now.entry) }).then(function (r) {
            // A 4xx is a refusal: nothing was added. A 5xx is ambiguous (the
            // add may have landed), so it takes the same bounded chase as a
            // dropped connection (the catch below, addAttempted is set).
            if (isRefusal(r)) return 'failed';
            if (!r || !r.ok) throw httpError('/cart/add.js', r);
            return r.json().catch(function () { return null; }).then(function (added) {
              addLanded = true;
              var line = added && added.items ? added.items[0] : added;
              if (isFreeLine(line)) return 'won';
              return readCart().then(function (after) {
                confirmedCart = after;
                return slotView(group, after).free ? 'won' : 'charged';
              }, function () { confirmedCart = null; return 'charged'; });
            });
          });
        }).then(function (outcome) {
          setAdding(group, false);
          if (outcome === 'failed') { announce(group, COPY.msgFailed, ['won', 'none']); return; }
          if (outcome === 'none') { endWithout(group, COPY.msgGone, ['won', 'claim']); return; }
          if (outcome === 'charged') { chargedEnding(group, confirmedCart, addLanded); render(group, 'none', { byShopper: true }); return; }
          claimWon(group);
          return readCart().then(wake, function () {});
        });
      }).catch(function () {
        if (!addAttempted) {
          setAdding(group, false);
          announce(group, COPY.msgFailed, ['won', 'none']);
          return;
        }
        return chaseCart(SWEEP_TRIES, function (cart) {
          var view = slotView(group, cart);
          if (taggedChargedGift(cart)) { setAdding(group, false); chargedEnding(group, cart, true); render(group, 'none', { byShopper: true }); return true; }
          if (!view.line) return false;
          if (view.free) { setAdding(group, false); claimWon(group); wake(cart); return true; }
          setAdding(group, false);
          endWithout(group, COPY.msgAlready, ['won', 'claim']);
          return true;
        }).catch(function () { window.location.reload(); });
      });
    }

    function update(cart) {
      var snap = engine.snapshot(cart);
      var wanted = {};
      if (snap.mode === MODE_TIERED) {
        snap.decision.earned.forEach(function (entry) {
          if (!isLegacySlot(entry)) wanted[slotKey(entry.tier, entry.slot)] = entry;
        });
      }
      Object.keys(wanted).forEach(function (key) {
        var entry = wanted[key];
        var existing = groups[key];
        if (existing && existing.variant !== entry.variant_id && !existing.adding) {
          // The slot's gift changed (phase boundary, retirement): rebuild.
          if (existing.wrap.parentNode) existing.wrap.parentNode.removeChild(existing.wrap);
          existing = null;
        }
        if (!existing) {
          groups[key] = buildGroup(key, entry, cart);
          root.insertBefore(groups[key].wrap, announcer);
        }
      });
      Object.keys(groups).forEach(function (key) {
        render(groups[key], stateFor(groups[key], cart));
      });
      // A group whose slot is no longer earned (the old side of a tier swap)
      // and has nothing left to show is removed, so no empty zero-height
      // .fg-tier stays behind (live QA, PR #426). A group still showing
      // something (a free line the page cannot confirm is earned, a status
      // message the shopper may be reading) or with an add in flight stays.
      Object.keys(groups).forEach(function (key) {
        var group = groups[key];
        if (hasOwn(wanted, key) || group.adding) return;
        if (group.wrap.getAttribute('data-fg-state') !== 'none' || group.status.textContent) return;
        if (group.wrap.contains(document.activeElement)) return;
        if (group.wrap.parentNode) group.wrap.parentNode.removeChild(group.wrap);
        delete groups[key];
      });
      // The first render describes the page as loaded and is silent; from then
      // on, state changes the shopper did not cause go to the announcer.
      first = false;
    }

    return update;
  }

  // PDP: the legacy "Free gift with your luggage" callout is placed exactly as
  // before (FG:277-285). In tiered mode the active window's extra T0 gifts are
  // listed under it, and the spend tiers get the gift ladder card (#604,
  // which replaced the single next-step card of #480), available gifts only
  // (G10).
  function placePdpCallout() {
    var tpl = document.querySelector('template[data-free-gift-pdp]');
    if (!tpl || document.querySelector('.fg-pdp')) return;
    var anchor = document.querySelector('[data-product-form] .product-form__buttons, product-form .product-form__buttons, .product-form__buttons, [data-add-to-cart]');
    if (!anchor) return;
    var node = tpl.content.firstElementChild.cloneNode(true);
    var host = anchor.closest('.product-form__buttons') || anchor.parentElement;
    host.parentElement.insertBefore(node, host.nextSibling);
  }

  // Extra T0 gifts of the active window (a BFCM kit's second piece). The spend
  // tier is the card below, not a line here.
  function pdpLines(snap, cfg) {
    var decision = snap.decision;
    var win = decision.detail && decision.detail.window;
    if (decision.mode !== MODE_TIERED || !win || !decision.detail.ctx) return [];
    var lines = [];
    var t0tier = findTier(win, T0_KEY);
    if (t0tier) {
      t0tier.slots.forEach(function (slot, index) {
        if (index === 0) return;
        var gid = resolveSlotGift(slot, decision.detail.today, decision.detail.ctx);
        var entry = gid ? poolEntry(cfg, gid) : null;
        if (entry && entry.available && entry.title) lines.push(fill(COPY.pdpAlso, { gift: displayName(entry.title, true) }));
      });
    }
    return lines;
  }

  // A price as the theme hands it over: cents as a number (product JSON), or
  // a string of cents or of dollars ("149.99").
  function priceToCents(value) {
    if (typeof value === 'number') return isFinite(value) ? Math.round(value) : null;
    var text = String(value == null ? '' : value).trim();
    if (!/^\d+(\.\d+)?$/.test(text)) return null;
    return text.indexOf('.') >= 0 ? Math.round(parseFloat(text) * 100) : parseInt(text, 10);
  }

  /*
   * The PDP gift ladder (#604), which replaces the single next-step card of
   * #480 at the same place and under the same conditions: a tiered window
   * with an active window, this product counts toward the gift (or the cart
   * already qualifies), and a usable price for it. It counts the cart as it
   * is plus one of this product at its selected price (the same pre-discount
   * line total the discount uses), and lists every usable step of the
   * window's ladder with its state (ladderView). No steps (outside a deal
   * window, every step's gift sold out): no card at all (F1/F2).
   */
  function pdpLadderView(snap, cfg, priceCents) {
    var decision = snap.decision;
    var win = decision.detail && decision.detail.window;
    if (decision.mode !== MODE_TIERED || !win || !decision.detail.ctx) return null;
    var q = cfg && cfg.tier && cfg.tier.qualifiers ? cfg.tier.qualifiers[win.qualifier] : null;
    // This product has to count toward the gift (or the cart already does).
    if (!decision.detail.qualifies && !(q && q.pageQualifies)) return null;
    // No usable price for this product: no card, rather than a wrong amount.
    var cents = priceToCents(priceCents);
    if (cents === null) return null;
    // No rows (outside a deal window, every gift sold out, or a $360 cart
    // after the top gift sold out, where ladderView drops the passed rows):
    // no card.
    var ladder = ladderView(snap, cfg, cents);
    return ladder && ladder.steps.length ? ladder : null;
  }

  // What kind of gift the ladder offers, for its words. "bag" only when every
  // usable gift of every shown step is named as a bag; a step giving more
  // than one gift makes it "gifts"; anything else is "gift". Names, not
  // product types, because the gift pool carries titles only. The product
  // name (before " · color") must END in the bag word, so "Everyday Sling",
  // "Breenon Backpack" and "The Weekender Bag" are bags, while accessories
  // such as "Sling Strap" or "Backpack Rain Cover" are not.
  var BAG_NAME = /\b(bag|backpack|sling|duffel|duffle|tote|weekender)s?$/i;
  function isBagName(title) {
    var name = displayName(title, true).split(' · ')[0].trim();
    return BAG_NAME.test(name);
  }
  function ladderNoun(view) {
    var multi = view.steps.some(function (step) { return step.info.usableGifts.length > 1; });
    if (multi) return 'gifts';
    var allBags = view.steps.every(function (step) {
      return step.info.usableGifts.every(function (gift) { return isBagName(gift.title); });
    });
    return allBags ? 'bag' : 'gift';
  }

  // The ladder's words: the heading, one title and one status per step, and
  // the footnote. No dollar value for any gift and no "add $X more" (Kevin
  // 2026-11-01): each step says its threshold only. A multi-slot step names
  // every usable gift it gives ("$250 or more: A + B"), and then the words
  // say "gifts", not "one gift".
  function ladderCopy(view, currency) {
    var noun = ladderNoun(view);
    // "the bag below" names what the CURRENT step gives: plural only when
    // that step itself gives more than one gift.
    var currentStep = null;
    view.steps.forEach(function (step) { if (step.state === 'current') currentStep = step; });
    var below = currentStep && currentStep.info.usableGifts.length > 1 ? 'gifts' : (noun === 'gifts' ? 'gift' : noun);
    return {
      heading: COPY.ladderHeading[noun],
      footnote: COPY.ladderFootnote[noun],
      rows: view.steps.map(function (step) {
        var status;
        var item = step.info.usableGifts.length > 1 ? COPY.ladderItem.gifts : COPY.ladderItem[noun === 'gifts' ? 'gift' : noun];
        if (step.state === 'current') {
          status = fill(COPY.ladderCurrent, { zero: formatZero(currency) });
        } else if (step.state === 'passed') {
          status = fill(COPY.ladderPassed, { noun: below });
        } else {
          status = fill(view.current ? COPY.ladderAheadInstead : COPY.ladderAhead, { threshold: formatMoney(step.threshold, currency), item: item });
        }
        return {
          key: step.key,
          state: step.state,
          title: fill(COPY.ladderStep, {
            threshold: formatMoney(step.threshold, currency),
            gift: step.info.usableGifts.map(function (gift) { return displayName(gift.title, true); }).join(COPY.ladderGiftJoin)
          }),
          status: status
        };
      })
    };
  }

  // Inline styles per step state. The state is always said in words (the
  // status line); colour, weight and the check only reinforce it. Text
  // contrast on #fbfdfc / #eef5f1: #0E3D2D 11.9 / 11.0, #41544b 7.9,
  // #5f7168 5.1 (all above 4.5:1).
  var LADDER_ROW = 'display:flex; align-items:center; gap:10px; margin:0; padding:6px; border-radius:8px; list-style:none;';
  var LADDER_STATE = {
    current: { row: 'background:#eef5f1;', title: 'color:#0E3D2D;', status: 'color:#0E3D2D; font-weight:700;', tick: true },
    ahead: { row: 'background:transparent;', title: 'color:#0E3D2D;', status: 'color:#41544b; font-weight:400;', tick: false },
    passed: { row: 'background:transparent;', title: 'color:#5f7168;', status: 'color:#5f7168; font-weight:400;', tick: false }
  };
  var LADDER_TITLE = 'margin:0; font-size:13.5px; font-weight:700; line-height:1.3; overflow-wrap:anywhere;';
  var LADDER_STATUS = 'margin:2px 0 0; font-size:12.5px; line-height:1.35; display:flex; align-items:baseline; gap:6px;';
  // A check drawn with two borders: no icon font, nothing to load, nothing
  // that animates. aria-hidden; the status words say the same.
  var LADDER_TICK = 'flex:0 0 auto; display:inline-block; width:5px; height:9px; margin:0 2px 0 3px; border:solid #0E3D2D; border-width:0 2px 2px 0; transform:rotate(45deg); transform-origin:center;';
  var ladderSeq = 0;

  // What decides whether the card is rebuilt rather than updated in place:
  // which steps there are, their usable gifts (so the footnote too) and their
  // pictures.
  function ladderStructure(view, currency) {
    return currency + '#' + view.steps.map(function (step) {
      var gifts = step.info.usableGifts.map(function (gift) { return gift.gid + '~' + gift.title; }).join(',');
      return [step.key, step.threshold, gifts, step.info.image || step.info.imageLarge || ''].join('|');
    }).join('#');
  }

  /*
   * The ladder card. Styled like the T0 "luggage tag" callout above it
   * (#0E3D2D text, rgba(14,61,45,.28) border, 10px radius, #fbfdfc, the
   * orange eyebrow) and compact enough for 375px. Accessibility (WCAG 2.2
   * AA): an ordered list named by the heading (role="list" kept explicitly,
   * WebKit drops list semantics under list-style:none); every state in
   * words; images are decorative (alt=""); NOT a live region, as before: it
   * changes with the variant and the cart, and the add-to-cart flow already
   * speaks for itself. Its one focusable element is the "Offer details" link
   * in the footnote; the card is rebuilt only when its steps change (a gift
   * sells out, a phase rolls over), and bindPdp then moves focus to the new
   * link if it was on the old one, so a rebuild never drops focus (2.4.3).
   */
  function offerHref() {
    var root = '/';
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
      root = window.Shopify.routes.root;
    }
    return root.replace(/\/?$/, '/') + COPY.ladderOfferPath;
  }
  function buildPdpLadder(view, currency) {
    ladderSeq += 1;
    var headingId = 'fg-ladder-h-' + ladderSeq;
    var words = ladderCopy(view, currency);
    var rows = {};
    var items = view.steps.map(function (step) {
      var src = step.info.image || step.info.imageLarge;
      var tile = el('span', { 'aria-hidden': 'true', style: 'flex:0 0 auto; display:flex; align-items:center; justify-content:center; width:40px; height:40px; border-radius:6px; background:#eef5f1; overflow:hidden;' }, [
        src ? el('img', { src: src, alt: '', width: '36', height: '36', loading: 'lazy', style: 'width:36px; height:36px; object-fit:contain; mix-blend-mode:multiply;' }) : null
      ]);
      var title = el('p', { 'data-fg-ladder-title': '', style: LADDER_TITLE });
      var tick = el('span', { 'aria-hidden': 'true', 'data-fg-ladder-tick': '', style: LADDER_TICK + ' display:none;' });
      var text = el('span', { 'data-fg-ladder-status-text': '' });
      var status = el('p', { 'data-fg-ladder-status': '', style: LADDER_STATUS }, [tick, text]);
      var li = el('li', { 'data-fg-ladder-step': step.key, style: LADDER_ROW }, [
        tile,
        el('div', { style: 'flex:1 1 auto; min-width:0;' }, [title, status])
      ]);
      rows[step.key] = { li: li, title: title, status: status, tick: tick, text: text };
      return li;
    });
    var node = el('div', { 'class': 'fg-pdp-ladder', 'data-fg-pdp-ladder': '', style: 'margin:-4px 0 14px; padding:10px 12px; border:1px solid rgba(14,61,45,.28); border-radius:10px; background:#fbfdfc; color:#0E3D2D;' }, [
      el('p', { id: headingId, style: 'margin:0 0 4px; font-size:10.5px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; line-height:1.4; color:#BF3D1E;', text: words.heading }),
      el('ol', { role: 'list', 'aria-labelledby': headingId, style: 'list-style:none; margin:0 -6px; padding:0;' }, items),
      el('p', { 'data-fg-ladder-footnote': '', style: 'margin:6px 0 0; font-size:12px; line-height:1.4; color:#41544b;' }, [
        document.createTextNode(words.footnote + ' '),
        el('a', { href: offerHref(), 'data-fg-ladder-offer': '', style: 'color:inherit; text-decoration:underline; text-underline-offset:2px;', text: COPY.ladderOfferLink })
      ])
    ]);
    var ladder = { node: node, rows: rows };
    paintPdpLadder(ladder, view, currency);
    return ladder;
  }

  // States and words, updated in place (the rows themselves do not move).
  function paintPdpLadder(ladder, view, currency) {
    ladderCopy(view, currency).rows.forEach(function (row) {
      var dom = ladder.rows[row.key];
      if (!dom) return;
      var look = LADDER_STATE[row.state] || LADDER_STATE.ahead;
      if (dom.li.getAttribute('data-fg-ladder-state') !== row.state) {
        dom.li.setAttribute('data-fg-ladder-state', row.state);
        dom.li.setAttribute('style', LADDER_ROW + ' ' + look.row);
        dom.title.setAttribute('style', LADDER_TITLE + ' ' + look.title);
        dom.status.setAttribute('style', LADDER_STATUS + ' ' + look.status);
        dom.tick.setAttribute('style', LADDER_TICK + (look.tick ? '' : ' display:none;'));
      }
      if (dom.title.textContent !== row.title) dom.title.textContent = row.title;
      if (dom.text.textContent !== row.status) dom.text.textContent = row.status;
    });
  }

  function bindPdp(engine, doc) {
    var d = doc || document;
    var callout = d.querySelector('.fg-pdp');
    if (!callout) return function () {};
    var tpl = d.querySelector('template[data-free-gift-pdp]');
    var price = priceToCents(tpl ? tpl.getAttribute('data-fg-pdp-price') : null);
    var ownVariants = String((tpl && tpl.getAttribute('data-fg-pdp-variants')) || '').split(',').filter(Boolean);
    var latest = null;
    var box = null;
    var lastKey = null;
    var ladder = null;
    var ladderKey = null;
    function render() {
      if (!latest) return;
      var cart = latest;
      var currency = cart.currency || 'USD';
      var snap = engine.snapshot(cart);
      retitleCallout(callout, snap, engine.cfg, currency);
      var lines = pdpLines(snap, engine.cfg);
      var key = lines.join('\n');
      if (key !== lastKey) {
        lastKey = key;
        if (!lines.length) {
          if (box && box.parentNode) box.parentNode.removeChild(box);
          box = null;
        } else {
          var fresh = el('div', { 'class': 'fg-pdp-extra', 'data-fg-pdp-extra': '', style: 'margin:-6px 0 14px; padding:8px 12px; border:1px dashed rgba(14,61,45,.35); border-radius:8px; background:#fbfdfc; font-size:13px; line-height:1.4; color:#0E3D2D;' },
            lines.map(function (text) { return el('p', { style: 'margin:0;', text: text }); }));
          if (box && box.parentNode) box.parentNode.replaceChild(fresh, box);
          else callout.parentNode.insertBefore(fresh, callout.nextSibling);
          box = fresh;
        }
      }
      // The gift ladder (#604): rebuilt when its steps change (a gift sells
      // out, a phase rolls over), otherwise updated in place.
      var view = price === null ? null : pdpLadderView(snap, engine.cfg, price);
      var nextKey = view ? ladderStructure(view, currency) : '';
      if (nextKey !== ladderKey) {
        // The "Offer details" link is the card's only focusable element: if
        // focus is on it when the card is rebuilt, it moves to the new link.
        var oldLink = ladder && ladder.node.querySelector ? ladder.node.querySelector('[data-fg-ladder-offer]') : null;
        var hadFocus = !!(oldLink && d.activeElement && d.activeElement === oldLink);
        if (ladder && ladder.node.parentNode) ladder.node.parentNode.removeChild(ladder.node);
        ladder = null;
        ladderKey = nextKey;
        if (view) {
          ladder = buildPdpLadder(view, currency);
          var after = box && box.parentNode ? box : callout;
          after.parentNode.insertBefore(ladder.node, after.nextSibling);
          var newLink = hadFocus && ladder.node.querySelector ? ladder.node.querySelector('[data-fg-ladder-offer]') : null;
          if (newLink && newLink.focus) newLink.focus({ preventScroll: true });
        } else if (hadFocus) {
          // No card any more (every step gift sold out while the link had
          // focus): move focus to the nearest real control, Add to Cart just
          // above the gift box, rather than making a container a focus stop.
          var atc = d.querySelector('.product-form__buttons [data-add-to-cart], .product-form__buttons button[type="submit"], [data-add-to-cart]');
          if (atc && atc.focus) atc.focus({ preventScroll: true });
        }
      } else if (ladder && view) {
        paintPdpLadder(ladder, view, currency);
      }
    }
    // The selected variant sets the price this card counts (theme.js fires
    // theme:variant:change from the product form). Only this product's own
    // variants: another product form on the page (a quick view, an upsell)
    // must not move it.
    if (d.addEventListener) {
      d.addEventListener('theme:variant:change', function (event) {
        var variant = event && event.detail && event.detail.variant;
        if (!variant || (ownVariants.length && ownVariants.indexOf(String(variant.id)) === -1)) return;
        var cents = priceToCents(variant.price);
        if (cents === null || cents === price) return;
        price = cents;
        render();
      });
    }
    return function update(cart) {
      latest = cart;
      render();
    };
  }

  // Only when the active window's T0 slot 0 is NOT the Liquid-rendered gift
  // (a config that gives T0 a different variant, e.g. a BFCM kit). For the
  // evergreen and PBDD windows the two are the same variant and the callout
  // is never touched (R1).
  function retitleCallout(callout, snap, cfg, currency) {
    if (snap.mode !== MODE_TIERED || !snap.t0 || snap.t0.variantId === cfg.giftVariantId) return;
    var entry = poolEntry(cfg, variantGid(snap.t0.variantId));
    if (!entry || !entry.title) return;
    var value = formatMoney(entry.price || 0, currency);
    var title = callout.querySelector('[data-fg-pdp-title]');
    if (title) title.textContent = displayName(entry.title, false);
    var strike = callout.querySelector('[data-fg-pdp-value]');
    if (strike) strike.textContent = value;
    var sr = callout.querySelector('[data-fg-pdp-value-sr]');
    if (sr) sr.textContent = fill(COPY.regularPrice, { value: value });
    // A different, function-priced T0 (the BFCM kit) follows its window's
    // terms: one per order, not the evergreen cubes' one per household.
    var limit = callout.querySelector('[data-fg-pdp-limit]');
    if (limit && limit.textContent !== COPY.calloutLimitOrder) limit.textContent = COPY.calloutLimitOrder;
    var img = callout.querySelector('img');
    if (img && entry.imageLarge) img.setAttribute('src', entry.imageLarge);
  }

  /* ======================================================================
   * 6. BOOT
   * ====================================================================== */

  // A fetch that no script on this page has wrapped: the native fetch of a
  // hidden same-origin about:blank iframe (third-party cart hooks such as
  // Swym's patch only the top window's fetch and XHR). Same origin, so the
  // cart cookie goes with it; URLs are made absolute against this page. Made
  // on first use; if it cannot be made (or turns out wrapped too), null, and
  // the engine falls back to window.fetch, which is exactly today's
  // behaviour.
  var nativeFetch = null;
  var nativeFetchTried = false;
  var nativeFrame = null;
  function unwrappedFetch() {
    // A frame that something else took off the page has no working fetch:
    // make a fresh one rather than fail every sweep after it.
    if (nativeFetchTried && nativeFrame && !nativeFrame.isConnected) {
      nativeFetchTried = false;
      nativeFetch = null;
      nativeFrame = null;
    }
    if (nativeFetchTried) return nativeFetch;
    nativeFetchTried = true;
    try {
      var frame = nativeFrame = document.createElement('iframe');
      frame.setAttribute('data-tc-gift-fetch', '');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('title', 'Free gift engine helper');
      frame.style.display = 'none';
      (document.body || document.documentElement).appendChild(frame);
      var w = frame.contentWindow;
      if (w && typeof w.fetch === 'function' && !w.fetch._swymd) {
        nativeFetch = function (url, init) {
          return w.fetch.call(w, new URL(url, window.location.href).href, init);
        };
      }
    } catch (error) {
      nativeFetch = null;
    }
    return nativeFetch;
  }

  function browserApi() {
    return makeApi(function (url, init) { return fetch(url, init); }, unwrappedFetch);
  }

  // ?tc_gift_test=<token>[&tc_gift_date=YYYY-MM-DD] writes the test cart
  // attributes, ?tc_gift_test=off clears them (design 3.3). The token only
  // ever travels from the URL into the cart attribute; it is never printed.
  // The page then reloads without the parameters so Liquid can re-check the
  // token and print the testMode boolean.
  function handleTestLink() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return false; }
    if (!params.has('tc_gift_test')) return false;
    var value = params.get('tc_gift_test') || '';
    var date = params.get('tc_gift_date') || '';
    var attributes = value === 'off' || value === ''
      ? { _tc_gift_test: '', _tc_gift_test_date: '' }
      : { _tc_gift_test: value, _tc_gift_test_date: isDateString(date) ? date : '' };
    params.delete('tc_gift_test');
    params.delete('tc_gift_date');
    var query = params.toString();
    var clean = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    // The one write whose answer is deliberately not acted on: success or
    // failure, the page reloads, Liquid re-checks the token, and a write that
    // failed simply leaves test mode off.
    browserApi().post('/cart/update.js', { attributes: attributes }).catch(function () {}).then(function () {
      window.location.replace(clean);
    });
    return true;
  }

  var booted = false;
  function boot() {
    if (booted || typeof document === 'undefined') return null;
    var cfgEl = document.querySelector('[data-free-gift-config]');
    if (!cfgEl) return null;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return null; }
    booted = true;
    if (handleTestLink()) return null;

    var api = browserApi();
    var engine = createEngine({
      cfg: cfg,
      api: api,
      dispatch: function (name, detail) {
        document.dispatchEvent(detail ? new CustomEvent(name, { detail: detail, bubbles: true }) : new CustomEvent(name, { bubbles: true }));
      },
      setTimeout: function (fn, ms) { return setTimeout(fn, ms); },
      today: function () { return storeToday(new Date()); },
      log: function (error) { if (window.console && console.warn) console.warn('[tc-gift-engine]', error); }
    });

    // UI subscribers.
    var tierRoot = document.querySelector('[data-fg-tiers]');
    if (tierRoot) engine.subscribe(bindTierCards(tierRoot, engine, api));
    Array.prototype.forEach.call(document.querySelectorAll('[data-fg-progress]'), function (node) {
      var latest = null;
      var surface = node.getAttribute('data-fg-progress') || 'cart';
      var box = node.closest ? node.closest('[data-fg-progress-box]') : null;
      var schedule = bindProgress(node, function () {
        return latest ? progressView(engine.snapshot(latest), cfg, latest.currency, surface) : null;
      }, engine.isSettled, function (view) { paintProgressBox(box, view); });
      engine.subscribe(function (cart) { latest = cart; schedule(); });
    });

    wire(engine, document, window, function (fn, ms) { return setTimeout(fn, ms); });

    placePdpCallout();
    engine.subscribe(bindPdp(engine));

    window.TCGiftEngine.instance = engine;
    // The legacy T0 card script runs before this deferred file; it waits for
    // this event to ask offersLegacyT0 (#479 item 2).
    document.dispatchEvent(new CustomEvent('tc-gift-engine:ready', { bubbles: true }));
    engine.run();
    return engine;
  }

  // Triggers, as before (FG:268-274): every theme:cart:change (300 ms) and
  // add-to-cart clicks (1500 ms), plus the load-time run in boot(). New:
  // pageshow for bfcache restores. theme.liquid's window.onpageshow already
  // dispatches theme:cart:change on every pageshow; this listener keeps the
  // engine awake even if some other script reassigns window.onpageshow, and
  // the busy/re-run pair coalesces the two.
  // Runs are scheduled through engine.schedule (tracked by isSettled); an
  // engine without it falls back to `later`.
  function wire(engine, doc, win, later) {
    var defer = engine.schedule ? function (ms) { engine.schedule(ms); } : function (ms) { later(engine.run, ms); };
    doc.addEventListener('theme:cart:change', function (e) {
      var detail = e && e.detail;
      if (detail && detail.cart && detail.cart.items) engine.notify(detail.cart);
      if (detail && detail.tcGiftEngine) return; // our own dispatch: never re-run on it
      defer(300);
    });
    doc.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-add-to-cart], form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] [type="button"]');
      if (btn) defer(1500);
    }, true);
    win.addEventListener('pageshow', function (e) { if (e && e.persisted) defer(0); });
  }

  function autoBoot() {
    if (typeof document === 'undefined') return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  return {
    // decision core (shared-vector surface)
    computeTargets: computeTargets,
    computeTierGifts: computeTierGifts,
    decide: decide,
    validateConfig: validateConfig,
    selectWindow: selectWindow,
    resolveSlotGift: resolveSlotGift,
    isRetired: isRetired,
    parseTimestamp: parseTimestamp,
    earnedTiers: earnedTiers,
    COMPILED_QUALIFIERS: COMPILED_QUALIFIERS,
    // theme helpers
    neutralLines: neutralLines,
    storeToday: storeToday,
    snapshot: snapshot,
    legacyT0Offered: legacyT0Offered,
    eventT0Target: eventT0Target,
    t0AddDue: t0AddDue,
    planSweep: planSweep,
    reofferKeys: reofferKeys,
    ladderView: ladderView,
    progressText: progressText,
    progressView: progressView,
    paintProgressBox: paintProgressBox,
    pdpLines: pdpLines,
    pdpLadderView: pdpLadderView,
    ladderCopy: ladderCopy,
    bindPdp: bindPdp,
    priceToCents: priceToCents,
    bindTierCards: bindTierCards,
    bindProgress: bindProgress,
    formatMoney: formatMoney,
    displayName: displayName,
    COPY: COPY,
    STAMPS: {
      DECISION: DECISION,
      TIER_DECISION: TIER_DECISION,
      ADDED: STAMP_ADDED,
      READDED: STAMP_READDED,
      REMOVED: STAMP_REMOVED,
      SWEPT: STAMP_SWEPT,
      NOT_ADDED: STAMP_NOT_ADDED
    },
    RETRY_LIMIT: RETRY_LIMIT,
    // engine + boot
    makeApi: makeApi,
    isRefusal: isRefusal,
    createEngine: createEngine,
    wire: wire,
    boot: boot,
    autoBoot: autoBoot
  };
});
